import { useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { Send, Mail, X } from 'react-feather';
import { ArrowCircleDown, PencilSimple } from '@phosphor-icons/react/dist/ssr';
import {
  CurrencyDollar,
  Warning,
  UsersThree,
  Percent,
  FileText,
  Bell,
  Info,
  Eye,
  TrendDown,
  TrendUp,
  Minus,
  Flag,
  UserCirclePlus,
  Link as LinkIcon,
} from 'phosphor-react';
import { Tooltip as ReactTooltip } from 'react-tooltip';

import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import {
  getSupplierSummary,
  getUserCustomers,
  recursiveGetUserCustomers,
  sendEmailStatementsForAllBuyers,
  sendEmailStatementsForBuyers,
  saveMonthlyStatementSnapshots,
  sendAllBuyersStatementFromSnapshot,
  clearStatementSendResults,
} from '../../../../redux/services/suppliersService';
import {
  getSupplierCollectionSequences,
  updateCollectionSequence,
} from '../../../../redux/services/collectionSequenceService';
import { getSupplierCollectionTriggers } from '../../../../redux/services/collectionTriggerService';
import { getSupplierPromiseToPays } from '../../../../redux/services/promiseToPayService';
import {
  getCustomerCollectionViews,
  createCustomerCollectionView as createCustomerCollectionViewThunk,
  updateCustomerCollectionView as updateCustomerCollectionViewThunk,
  deleteCustomerCollectionView as deleteCustomerCollectionViewThunk,
} from '../../../../redux/services/customerCollectionViewsService';
import {
  AgingBucketKey,
  PromiseToPayFilterValue,
  FlagFilterValue,
  QuickFilters,
  StoredQuickFilters,
  CustomerCollectionViewWithSummary,
  BUCKET_QUICK_FILTERS,
  PROMISE_TO_PAY_QUICK_FILTERS,
  FLAG_QUICK_FILTERS,
  EMPTY_QUICK_FILTERS,
  quickFiltersToStored,
  storedToQuickFilters,
  areStoredFiltersEqual,
  describeStoredFilters,
  formatAmountRangeLabel,
  isAmountRangeActive,
  CollectionViewFormModal,
  CollectionViewTabs,
} from './CollectionViews';
import { getSupplierCreditApplicationsMonitor } from '../../../../redux/services/creditApplicationsMonitorService';
import { onValue, ref as rtdbRef, remove } from 'firebase/database';
import type { Unsubscribe } from 'firebase/database';
import { myRealtimeDB } from '../../../../firebase/config';
import { getSupplierCustomerArReport } from '../../../../redux/services/reportsService';
import { getSupplierPendingUserLoginAttemptsCount } from '../../../../redux/services/userLoginAttemptsService';
import { getSupplierSalesReps } from '../../../../redux/services/supplierAdditionalDetailsService';

import { UserLoginAttemptsModal } from '../../userLoginAttempts/UserLoginAttemptsModal/UserLoginAttemptsModal';
import { SyncHistoryModal } from '../../syncHistory/SyncHistoryModal/SyncHistoryModal';
import { getSupplierSyncHistory } from '../../../../redux/services/syncHistoryService';
import { openMessageModal } from '../../../../redux/globalSlices/genericSlice';
import {
  updateCustomerBuyerFlags,
  updateCustomerBuyerAssignment,
} from '../../../../redux/globalSlices/suppliersSlice';
import { buildSupplierPath, hashStringToRange, openDillToast } from '../../../../utils/helpers';
import {
  updateSupplierBuyer,
  bulkSetBuyerFlag,
} from '../../../../redux/services/supplierBuyersService';
import { COLORS } from '../../../../utils/colors';
import { APP_ENVIRONMENT } from '../../../../utils/constants';
import {
  formatMoney,
  getCurrentTimezoneDate,
  timeSince,
  Buyer,
  DocumentsSummary,
  AgingRange,
  DEFAULT_AGING_RANGES,
  CreditApplicationMonitor,
  triggerAppliesToBuyer,
  sequenceAllowsBuyer,
  SalesRep,
  CustomerCollectionView,
  CustomerCollectionViewCreate,
  DeliveryAddress,
} from '@dill/dill-shared';
import momenttz from 'moment-timezone';

import {
  DillTable,
  DillButton,
  DillAvatar,
  DillModal,
  DillSelect,
  DillDropDownFilter,
  DillCheckbox,
} from '../../../general/Dill';
import type { DillColumn, DillSummaryCard } from '../../../general/Dill';
import AppSendStatementModal from '../../../general/AppSendStatementModal/AppSendStatementModal';
import AppGetPortalLinkModal from '../../../general/AppGetPortalLinkModal/AppGetPortalLinkModal';
import AppSaveStatementSnapshotModal from '../../../general/AppSaveStatementSnapshotModal/AppSaveStatementSnapshotModal';
import AppSendAllStatementsModal from '../../../general/AppSendAllStatementsModal/AppSendAllStatementsModal';
import AppStatementSendResultsModal, {
  type StatementSendResult,
} from '../../../general/AppStatementSendResultsModal/AppStatementSendResultsModal';
import CollectionActivityTimeline from '../../suppliers/CollectionActivityTimeline/CollectionActivityTimeline';
import moment from 'moment';

// ─── Row shape ────────────────────────────────────────────────────────────────

interface CustomerRow {
  id: string;
  name: string;
  ref: string;
  pastDue90: number;
  pastDue61: number;
  pastDue31: number;
  pastDue1: number;
  current: number;
  total: number;
  totalPastDue: number;
  creditLimit: number;
  creditUtil: number | null;
  pastDuePct: number;
  emails: {
    email: string;
    userId: string;
    isStatementsEmailOptOut: boolean;
    isPaymentRemindersEmailOptOut: boolean;
  }[];
  lastEmailed: Date | null;
  lastLogin: string | null;
  notes: string;
  isOverdue: boolean;
  creditManagerId: string | null;
  creditManagerName: string;
  salesRepId: string | null;
  branch: string | null;
  terms: string | null;
  isBuyerOnCreditHold: boolean;
  isFlagged: boolean;
  isAutopayActive: boolean;
  autopayFrequency: string | null;
  agingBuckets: { order: number; amount: number }[];
  lastCollectionActivity: {
    date: string | null;
    type: string | null;
    summaryLine: string | null;
    countLast30Days: number;
  } | null;
  // Precomputed daily onto the buyer doc (see generateBuyerMonthlyAnalyticsScheduledFunction) -
  // null until the first run for a buyer, or if there isn't enough payment history yet.
  dso: number | null;
  dsoTrend: 'IMPROVING' | 'DECLINING' | 'STABLE' | null;
  dsoChangeInDays: number | null;
  paymentBehaviorSummary: string | null;
  paymentBehaviorConfidence: 'HIGH' | 'MEDIUM' | 'LOW' | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fN = (n: number, min = 2) =>
  n === 0
    ? ''
    : n.toLocaleString('en-US', { maximumFractionDigits: min, minimumFractionDigits: min });

// Some date-ish fields coming off Buyer docs (e.g. an unconverted Firestore Timestamp)
// can't be parsed into a valid Date - guard against that instead of letting
// `.toISOString()` throw and take down the whole page.
const toSafeISOString = (value: unknown): string | null => {
  if (!value) return null;
  const date = new Date(value as any);
  return isNaN(date.getTime()) ? null : date.toISOString();
};

// Demo-only: loosely match a customer name against a credit monitoring company name
// (strips punctuation/legal suffixes so e.g. "Acme Inc." ~= "Acme, Inc").
const normalizeCompanyName = (name: string) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\b(inc|llc|ltd|corp|co|company)\b/g, '')
    .trim();

// Human-readable labels for collection activity types, shown in the "Collections Activity"
// column (see the fuller icon/color config in CollectionActivityTimeline.tsx).
const COLLECTION_ACTIVITY_TYPE_LABELS: Record<string, string> = {
  PAYMENT_REQUEST: 'Payment Request',
  PAYMENT_REMINDER: 'Payment Reminder',
  PAYMENT: 'Payment',
  REFUND: 'Refund',
  STATEMENT: 'Statement',
  DISPUTE: 'Dispute',
  PHONE_CALL: 'Phone Call',
  TEXT_MESSAGE: 'Text Message',
  NOTE: 'Note',
  TASK: 'Task',
  PROMISE_TO_PAY: 'Promise to Pay',
};

const parseNacmScore = (value: unknown): number | null => {
  const num = Number(value);
  return Number.isFinite(num) && num > 0 ? num : null;
};

// Demo-only: if a monitored company's name looks like this customer, use its real NACM
// score instead of the deterministic placeholder hash.
const getMonitoredCreditScore = (
  customerName: string,
  monitors: CreditApplicationMonitor[]
): number | null => {
  const target = normalizeCompanyName(customerName);
  if (!target) return null;
  const match = monitors.find((m) => {
    const candidate = normalizeCompanyName(m.companyName ?? '');
    if (!candidate) return false;
    return candidate === target || candidate.includes(target) || target.includes(candidate);
  });
  return match ? parseNacmScore((match.nacmReport as any)?.CmsCRScoreDetail?.Score) : null;
};

const getDemoCreditScoreTrend = (
  rowId: string
): { direction: 'UP' | 'DOWN' | 'STABLE'; pctChange: number } => {
  const directions: Array<'UP' | 'DOWN' | 'STABLE'> = ['UP', 'DOWN', 'STABLE'];
  const direction =
    directions[hashStringToRange(`${rowId}-creditScore-trend`, 0, directions.length - 1)];
  const pctChange =
    direction === 'STABLE' ? 0 : hashStringToRange(`${rowId}-creditScore-change`, 1, 12);
  return { direction, pctChange };
};

// Demo-only: deterministic per-row DSO + trend for the non-production "Average DSO" column,
// used when a buyer doesn't have dsoSummary precomputed yet (see the real field's comment on
// CustomerRow above).
const getDemoDso = (
  rowId: string
): { dso: number; trend: 'IMPROVING' | 'DECLINING' | 'STABLE'; changeInDays: number } => {
  const trends: Array<'IMPROVING' | 'DECLINING' | 'STABLE'> = ['IMPROVING', 'DECLINING', 'STABLE'];
  const trend = trends[hashStringToRange(`${rowId}-dso-trend`, 0, trends.length - 1)];
  // Signed the same way the real field is (see buyerMonthlyAnalyticsService's
  // `changeInDays = currentDso - priorAvg`): negative when DSO improved, positive when it
  // worsened, so the % change math below doesn't need to special-case demo vs. real data.
  const magnitude = hashStringToRange(`${rowId}-dso-change`, 1, 9);
  return {
    dso: hashStringToRange(`${rowId}-dso`, 18, 62),
    trend,
    changeInDays: trend === 'IMPROVING' ? -magnitude : trend === 'DECLINING' ? magnitude : 0,
  };
};

// Demo-only: realistic-looking payment-behavior summaries for the non-production "Payment
// Behavior" column, phrased to match what the real backend heuristic produces (see
// paymentBehaviorService.detectPaymentBehavior) - picked deterministically per row so it looks
// varied but stays stable across re-renders.
const DEMO_PAYMENT_BEHAVIOR_SUMMARIES = [
  'Pays generally around the 1st of the month',
  'Pays generally around the 15th of the month',
  'Pays generally around the end of the month',
  'Pays weekly',
  'Pays roughly every 2 weeks',
  'Pays roughly monthly',
  'No consistent payment pattern yet',
];

const getDemoPaymentBehaviorSummary = (rowId: string): string =>
  DEMO_PAYMENT_BEHAVIOR_SUMMARIES[
    hashStringToRange(`${rowId}-behavior`, 0, DEMO_PAYMENT_BEHAVIOR_SUMMARIES.length - 1)
  ];

function EmailsTooltip({
  emails,
  children,
}: {
  emails: CustomerRow['emails'];
  children: (props: {
    onMouseEnter: () => void;
    onMouseLeave: () => void;
    ref: React.RefObject<HTMLDivElement>;
  }) => React.ReactNode;
}) {
  const [isHovered, setIsHovered] = useState(false);
  const [position, setPosition] = useState({
    top: 0,
    left: 0,
    arrowTop: 0,
    side: 'right' as 'right' | 'left',
  });
  const triggerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearHideTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const handleTriggerMouseEnter = () => {
    clearHideTimeout();
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const TOOLTIP_W = 280;
      const isLeft = rect.right + 8 + TOOLTIP_W > window.innerWidth;
      const left = isLeft ? rect.left - TOOLTIP_W - 8 : rect.right + 8;
      // Estimate tooltip height and clamp so it doesn't overflow the viewport bottom
      const estimatedH = emails.length * 90 + 64;
      const rawTop = rect.top;
      const top = Math.max(8, Math.min(rawTop, window.innerHeight - estimatedH - 8));
      setPosition({
        top,
        left,
        arrowTop: rect.top - top + rect.height / 2,
        side: isLeft ? 'left' : 'right',
      });
    }
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    clearHideTimeout();
    timeoutRef.current = setTimeout(() => setIsHovered(false), 150);
  };

  useEffect(() => () => clearHideTimeout(), []);

  return (
    <>
      {children({
        onMouseEnter: handleTriggerMouseEnter,
        onMouseLeave: handleMouseLeave,
        ref: triggerRef,
      })}
      {isHovered &&
        createPortal(
          <div
            className="fixed bg-white border border-gray-100 shadow-2xl rounded-2xl py-3 px-3 z-[99999] w-[280px] overflow-y-auto"
            style={{
              top: `${position.top}px`,
              left: `${position.left}px`,
              maxHeight: `calc(100vh - ${position.top}px - 8px)`,
            }}
            onMouseEnter={clearHideTimeout}
            onMouseLeave={handleMouseLeave}
            onClick={(e) => e.stopPropagation()}>
            {/* Arrow pointing back to the trigger */}
            <div
              className="absolute w-3 h-3 bg-white border-gray-100"
              style={{
                top: position.arrowTop - 6,
                ...(position.side === 'right'
                  ? {
                      left: -6,
                      borderLeftWidth: 1,
                      borderBottomWidth: 1,
                      transform: 'rotate(45deg)',
                    }
                  : {
                      right: -6,
                      borderRightWidth: 1,
                      borderTopWidth: 1,
                      transform: 'rotate(45deg)',
                    }),
              }}
            />
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-2.5 px-1">
              {emails.length} email{emails.length !== 1 ? 's' : ''}
            </p>
            <div className="flex flex-col gap-2">
              {emails.map((email, ii) => (
                <div
                  key={ii}
                  className="bg-gray-50 border border-gray-100 rounded-xl px-3 py-2.5 flex flex-col gap-1.5">
                  <p className="text-xs font-medium text-gray-700 break-all leading-tight">
                    {email.email}
                  </p>
                  <div className="flex gap-1.5 flex-wrap">
                    <span
                      data-tooltip-id={`emails-stmts-modal-${ii}`}
                      className={`flex items-center gap-1 text-[10px] font-medium rounded-full px-2 py-0.5 border ${
                        !email.isStatementsEmailOptOut
                          ? 'bg-green-50 text-green-700 border-green-200'
                          : 'bg-gray-100 text-gray-400 border-gray-200 line-through'
                      }`}>
                      <FileText size={9} weight="bold" />
                      Statements
                    </span>
                    <span
                      data-tooltip-id={`emails-reminders-modal-${ii}`}
                      className={`flex items-center gap-1 text-[10px] font-medium rounded-full px-2 py-0.5 border ${
                        !email.isPaymentRemindersEmailOptOut
                          ? 'bg-blue-100 text-blue-700 border-blue-200'
                          : 'bg-gray-100 text-gray-400 border-gray-200 line-through'
                      }`}>
                      <Bell size={9} weight="bold" />
                      Reminders
                    </span>
                  </div>

                  <ReactTooltip
                    id={`emails-stmts-modal-${ii}`}
                    place="top"
                    variant="dark"
                    opacity="100%"
                    style={{ background: '#222', zIndex: 99999 }}>
                    <span className="text-xs">
                      {email.isStatementsEmailOptOut
                        ? 'Does not receive statements'
                        : 'Receives statements'}
                    </span>
                  </ReactTooltip>
                  <ReactTooltip
                    id={`emails-reminders-modal-${ii}`}
                    place="top"
                    variant="dark"
                    opacity="100%"
                    style={{ background: '#222', zIndex: 99999 }}>
                    <span className="text-xs">
                      {email.isPaymentRemindersEmailOptOut
                        ? 'Does not receive payment reminders'
                        : 'Receives payment reminders'}
                    </span>
                  </ReactTooltip>
                </div>
              ))}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}

function NoteModal({
  row,
  onClose,
  onSave,
  saving,
}: {
  row: CustomerRow;
  onClose: () => void;
  onSave: (id: string, note: string) => void;
  saving: boolean;
}) {
  const [note, setNote] = useState(row.notes);
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[300]">
      <div
        className="bg-white rounded-2xl shadow-2xl w-[460px] max-w-[92vw] p-6 flex flex-col gap-4"
        onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center gap-3">
          <div>
            <p className="text-sm font-semibold text-gray-700">
              {row.name}
              {row.ref && (
                <span className="text-gray-400 font-normal ml-1 text-xs">#{row.ref}</span>
              )}
            </p>
            <p className="text-xs text-gray-500">
              Total due:{' '}
              <span className={`font-semibold ${row.isOverdue ? 'text-red-500' : 'text-gray-700'}`}>
                {formatMoney(row.total, 2)}
              </span>
            </p>
          </div>
        </div>

        {/* Aging grid */}
        <div className="grid grid-cols-5 gap-2">
          {(
            [
              ['90+', row.pastDue90, true],
              ['61–90', row.pastDue61, true],
              ['31–60', row.pastDue31, false],
              ['1–30', row.pastDue1, false],
              ['Current', row.current, false],
            ] as [string, number, boolean][]
          ).map(([l, v, warn]) => (
            <div
              key={l}
              className={`rounded-lg p-2 border ${
                warn && v > 0 ? 'bg-red-50 border-red-100' : 'bg-blue-50 border-blue-100'
              }`}>
              <p
                className={`text-[9px] uppercase tracking-wide font-medium mb-0.5 ${
                  warn && v > 0 ? 'text-red-500' : 'text-blue-600'
                }`}>
                {l}
              </p>
              <p
                className={`text-xs font-semibold ${
                  warn && v > 0 ? 'text-red-500' : 'text-gray-700'
                }`}>
                {v > 0 ? formatMoney(v, 2) : '—'}
              </p>
            </div>
          ))}
        </div>

        {/* Note */}
        <div>
          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1 block">
            Notes
          </label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            className="w-full resize-y text-sm border border-gray-200 rounded-xl px-3 py-2 outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-50 transition-all"
          />
        </div>

        <div className="flex justify-end gap-2">
          <DillButton variant="secondary" label="Cancel" onClick={onClose} />
          <DillButton
            label={saving ? 'Saving…' : 'Save changes'}
            isLoading={saving}
            onClick={() => onSave(row.id, note)}
          />
        </div>
      </div>
    </div>
  );
}

function CollectionActivityModal({
  row,
  buyer,
  onClose,
}: {
  row: CustomerRow;
  buyer?: Buyer | null;
  onClose: () => void;
}) {
  return (
    <DillModal
      open
      onClose={onClose}
      title={row.name}
      subtitle={row.ref ? `#${row.ref}` : undefined}
      width="95vw"
      maxWidth="1200px"
      contentClassName="p-0 overflow-hidden">
      <div className="h-[75vh] px-5 py-4">
        <CollectionActivityTimeline buyerId={row.id} buyer={buyer} hideActions />
      </div>
    </DillModal>
  );
}

// Demo-only: compact speedometer-style gauge for the "Credit Score" column, sized to fit
// inline in a table cell (see the full-size version in NACMScoreCard for the same idea
// rendered larger).
function CreditScoreGauge({
  score,
  min,
  max,
  trend,
}: {
  score: number;
  min: number;
  max: number;
  trend?: { direction: 'UP' | 'DOWN' | 'STABLE'; pctChange: number };
}) {
  const color = score >= 740 ? '#16a34a' : score >= 670 ? '#f59e0b' : '#ef4444';
  const clamped = Math.min(max, Math.max(min, score));
  const pct = ((clamped - min) / (max - min)) * 100;

  const R = 28;
  const cx = 50;
  const cy = 40;
  const startAngle = Math.PI;
  const endAngle = startAngle + Math.PI * (pct / 100);

  const arcPath = (start: number, end: number) => {
    const s = { x: cx + R * Math.cos(start), y: cy + R * Math.sin(start) };
    const e = { x: cx + R * Math.cos(end), y: cy + R * Math.sin(end) };
    const large = end - start > Math.PI ? 1 : 0;
    return `M ${s.x} ${s.y} A ${R} ${R} 0 ${large} 1 ${e.x} ${e.y}`;
  };
  const knob = { x: cx + R * Math.cos(endAngle), y: cy + R * Math.sin(endAngle) };

  const trendColor =
    trend?.direction === 'UP' ? '#059669' : trend?.direction === 'DOWN' ? '#ef4444' : '#9ca3af';
  // Same baseline the score number uses (cy + 2), so the triangle/text sit right next to it.
  const trendBaseline = cy + 2;
  const trendX = cx + R + 10;

  return (
    <svg viewBox="0 0 134 48" width="113" height="40" className="shrink-0">
      <title>{`Credit score ${score} (range ${min}–${max})`}</title>
      <path
        d={arcPath(startAngle, startAngle + Math.PI)}
        fill="none"
        stroke="#E5E7EB"
        strokeWidth="5"
        strokeLinecap="round"
      />
      <path
        d={arcPath(startAngle, endAngle)}
        fill="none"
        stroke={color}
        strokeWidth="5"
        strokeLinecap="round"
      />
      <circle cx={knob.x} cy={knob.y} r="4" fill="white" stroke={color} strokeWidth="2" />
      <text x={cx} y={cy + 2} textAnchor="middle" fontSize="14" fontWeight="700" fill={color}>
        {score}
      </text>
      {trend && (
        <g>
          <title>
            {trend.direction === 'STABLE'
              ? 'Stable'
              : `${trend.direction === 'UP' ? 'Up' : 'Down'} ${trend.pctChange}%`}
          </title>
          {trend.direction === 'UP' && (
            <TrendUp
              x={trendX - 7}
              y={trendBaseline - 11}
              size={14}
              weight="bold"
              color={trendColor}
            />
          )}
          {trend.direction === 'DOWN' && (
            <TrendDown
              x={trendX - 7}
              y={trendBaseline - 11}
              size={14}
              weight="bold"
              color={trendColor}
            />
          )}
          {trend.direction === 'STABLE' && (
            <Minus
              x={trendX - 7}
              y={trendBaseline - 11}
              size={14}
              weight="bold"
              color={trendColor}
            />
          )}
          {trend.direction !== 'STABLE' && (
            <text x={trendX + 8} y={trendBaseline} fontSize="11" fontWeight="600" fill={trendColor}>
              {trend.pctChange}%
            </text>
          )}
        </g>
      )}
    </svg>
  );
}

function CreditUtilBar({ pct }: { pct: number }) {
  const isOver = pct > 100;
  return (
    <div className="flex items-center gap-2 w-full">
      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden min-w-0">
        <div
          className={`h-full rounded-full ${isOver ? 'bg-red-500' : 'bg-green-500'}`}
          style={{ width: `${Math.min(pct, 100)}%` }}
        />
      </div>
      <span
        className={`text-xs tabular-nums shrink-0 w-10 text-right ${
          isOver ? 'font-semibold text-red-600' : 'text-gray-600'
        }`}>
        {pct}%
      </span>
    </div>
  );
}

interface CreditManager {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

function CreditManagerModal({
  row,
  creditManagers,
  onClose,
  onSave,
  saving,
}: {
  row: CustomerRow;
  creditManagers: CreditManager[];
  onClose: () => void;
  onSave: (id: string, creditManagerId: string | null) => void;
  saving: boolean;
}) {
  const [selectedId, setSelectedId] = useState(row.creditManagerId ?? '');
  const existingManager = creditManagers.find((cm) => cm.id === row.creditManagerId);
  const hasExisting = !!existingManager;

  return (
    <DillModal
      open
      onClose={onClose}
      title={hasExisting ? 'Change Credit Manager' : 'Assign Credit Manager'}
      subtitle={`${row.name}${row.ref ? ` #${row.ref}` : ''}`}
      width="360px"
      footer={
        <>
          <DillButton variant="secondary" label="Cancel" onClick={onClose} />
          <DillButton
            label={saving ? 'Saving…' : 'Save'}
            isLoading={saving}
            onClick={() => onSave(row.id, selectedId || null)}
          />
        </>
      }>
      <div>
        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5 block">
          Select Credit Manager
        </label>
        <DillSelect
          value={selectedId}
          onChange={setSelectedId}
          placeholder="Select Credit Manager"
          options={[
            { value: '', label: '— No Credit Manager —' },
            ...creditManagers.map((cm) => ({
              value: cm.id,
              label: `${cm.firstName} ${cm.lastName}`,
            })),
          ]}
        />
      </div>
    </DillModal>
  );
}

function SalesRepModal({
  row,
  salesReps,
  onClose,
  onSave,
  saving,
}: {
  row: CustomerRow;
  salesReps: SalesRep[];
  onClose: () => void;
  onSave: (id: string, salesRepId: string | null) => void;
  saving: boolean;
}) {
  const [selectedId, setSelectedId] = useState(row.salesRepId ?? '');
  const existingRep = salesReps.find((sp) => sp.id === row.salesRepId);
  const hasExisting = !!existingRep;

  return (
    <DillModal
      open
      onClose={onClose}
      title={hasExisting ? 'Change Sales Rep' : 'Assign Sales Rep'}
      subtitle={`${row.name}${row.ref ? ` #${row.ref}` : ''}`}
      width="360px"
      footer={
        <>
          <DillButton variant="secondary" label="Cancel" onClick={onClose} />
          <DillButton
            label={saving ? 'Saving…' : 'Save'}
            isLoading={saving}
            onClick={() => onSave(row.id, selectedId || null)}
          />
        </>
      }>
      <div>
        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5 block">
          Select Sales Rep
        </label>
        <DillSelect
          value={selectedId}
          onChange={setSelectedId}
          placeholder="Select Sales Rep"
          options={[
            { value: '', label: '— No Sales Rep —' },
            ...salesReps.map((sp) => ({
              value: sp.id,
              label: sp.name,
            })),
          ]}
        />
      </div>
    </DillModal>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export const Customers = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { user, firebaseUserId } = useAppSelector((s) => s.auth);
  const currentSupplier = useAppSelector((s) => s.auth.currentSupplier);
  // Tracks which supplier the in-flight customers fetch belongs to, so a fetch started for
  // a previous supplier stops as soon as the supplier is switched. `inFlightCustomers
  // RequestRef` holds the latest dispatched request so it can be aborted on switch.
  const activeFetchSupplierIdRef = useRef<string | null>(null);
  const inFlightCustomersRequestRef = useRef<{ abort: () => void } | null>(null);
  const { customers, supplierUsers } = useAppSelector((s) => s.supplier);
  const { salesReps } = useAppSelector((s) => s.salesReps);
  const { isSupplierSyncing, lastSyncTimeSupplier } = useAppSelector((s) => s.generic);
  const { sequences: collectionSequences } = useAppSelector((s) => s.collectionSequences);
  const { triggers: collectionTriggers } = useAppSelector((s) => s.collectionTriggers);
  const { supplierPromiseToPays } = useAppSelector((s) => s.promiseToPays);
  // Demo-only: backs the non-production "Credit Score" column (see below).
  const { creditApplicationsMonitor } = useAppSelector((s) => s.creditApplicationsMonitor);
  const mainRoles = user?.roles.filter((r) => r !== 'DILL_ADMIN');

  const [loading, setLoading] = useState(false);
  const [openGetPortalLinkModal, setOpenGetPortalLinkModal] = useState(false);
  const [customerCount, setCustomerCount] = useState(0);
  const [prevLastDocId, setPrevLastDocId] = useState('');
  const [noteModal, setNoteModal] = useState<CustomerRow | null>(null);
  const [savingNote, setSavingNote] = useState(false);
  const [collectionActivityModal, setCollectionActivityModal] = useState<CustomerRow | null>(null);
  const [creditManagerModal, setCreditManagerModal] = useState<CustomerRow | null>(null);
  const [savingCreditManager, setSavingCreditManager] = useState(false);
  const [salesRepModal, setSalesRepModal] = useState<CustomerRow | null>(null);
  const [savingSalesRep, setSavingSalesRep] = useState(false);
  const [openSendStatementModal, setOpenSendStatementModal] = useState(false);
  const [sendStatementLoading, setSendStatementLoading] = useState(false);
  const [selectedRowForStatement, setSelectedRowForStatement] = useState<CustomerRow | null>(null);
  const [openSendAllModal, setOpenSendAllModal] = useState(false);
  const [sendAllLoading, setSendAllLoading] = useState(false);
  const [openSaveSnapshotModal, setOpenSaveSnapshotModal] = useState(false);
  const [saveSnapshotLoading, setSaveSnapshotLoading] = useState(false);
  const [sendResultsData, setSendResultsData] = useState<StatementSendResult | null>(null);
  const [, setTickMs] = useState(0);
  const [isUserLoginAttemptsModalOpen, setIsUserLoginAttemptsModalOpen] = useState(false);
  const [isSyncHistoryModalOpen, setIsSyncHistoryModalOpen] = useState(false);
  // flags a failed or partial latest sync next to "Synced X ago"
  const { latestSyncStatus } = useAppSelector((s) => s.syncHistory);

  useEffect(() => {
    if (currentSupplier?.id) {
      dispatch(getSupplierSyncHistory({ supplierId: currentSupplier.id }));
    }
  }, [currentSupplier?.id, dispatch]);

  // ── Portal access requests ──────────────────────────────────────────────────
  // People who tried to sign in but have no access. Actioned from this page because granting
  // access means using Account Access > Invite Users on a customer record.
  const { supplierPendingUserLoginAttemptsCount } = useAppSelector((s) => s.userLoginAttempts);

  useEffect(() => {
    if (currentSupplier?.id) {
      dispatch(getSupplierPendingUserLoginAttemptsCount({ supplierId: currentSupplier.id }));
    }
  }, [currentSupplier?.id, dispatch]);

  useEffect(() => {
    if (currentSupplier?.id && salesReps.length === 0) {
      dispatch(getSupplierSalesReps({ supplierId: currentSupplier.id }));
    }
  }, [currentSupplier?.id, dispatch]);

  // Lets the weekly unresolved login attempts email open the list directly
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('openLoginAttempts') === 'true') {
      setIsUserLoginAttemptsModalOpen(true);
      searchParams.delete('openLoginAttempts');
      const remaining = searchParams.toString();
      navigate(`${location.pathname}${remaining ? `?${remaining}` : ''}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // ── Bulk selection (checkboxes) → "Send Statements" / "Enroll in Collection Sequence" /
  // "Flag" / "Unflag" ──────────────────────────────────────────────────────────────────────
  const [selectedCustomerIds, setSelectedCustomerIds] = useState<Set<string | number>>(new Set());
  const [openBulkSendStatementModal, setOpenBulkSendStatementModal] = useState(false);
  const [bulkSendStatementLoading, setBulkSendStatementLoading] = useState(false);
  const [bulkSendStatementBuyerIds, setBulkSendStatementBuyerIds] = useState<string[]>([]);
  const [openBulkEnrollModal, setOpenBulkEnrollModal] = useState(false);
  const [bulkEnrollLoading, setBulkEnrollLoading] = useState(false);
  const [bulkEnrollBuyerIds, setBulkEnrollBuyerIds] = useState<string[]>([]);
  const [bulkEnrollSequenceId, setBulkEnrollSequenceId] = useState<string | null>(null);
  // "All Customers" always starts unfiltered - no restore-from-storage, on load or on switching
  // back to it from a view (see handleSelectView). Filters set during a session are never
  // persisted for this tab; only a named Collection View's filters are ever saved.
  const [quickFilters, setQuickFilters] = useState<QuickFilters>(EMPTY_QUICK_FILTERS);

  // ── Collection Views ──────────────────────────────────────────────────────
  // Backend-persisted (private per user) - see redux/globalSlices/customerCollectionViewsSlice.
  const { views: collectionViews, loadingList: collectionViewsLoadingList } = useAppSelector(
    (s) => s.customerCollectionViews
  );
  const isCreatingCollectionView = collectionViewsLoadingList.includes(
    'createCustomerCollectionView'
  );
  const isUpdatingCollectionView = collectionViewsLoadingList.includes(
    'updateCustomerCollectionView'
  );
  // null = the default "All Customers" tab.
  const [activeViewId, setActiveViewId] = useState<string | null>(null);
  // 'empty' = opened via the "+" tab (starts blank); 'current' = opened via "Save view" next to
  // the active-filters chips (starts pre-filled with whatever's currently active); null = closed.
  const [createViewModalPrefill, setCreateViewModalPrefill] = useState<'empty' | 'current' | null>(
    null
  );
  const [editingViewIdInModal, setEditingViewIdInModal] = useState<string | null>(null);

  // ── Last sync time ────────────────────────────────────────────────────────
  const lastSyncedDate = useMemo(() => {
    if (!lastSyncTimeSupplier) return null;
    const dates: Date[] = [];
    if (lastSyncTimeSupplier.customers)
      dates.push(momenttz(lastSyncTimeSupplier.customers).toDate());
    if (lastSyncTimeSupplier.invoices) dates.push(momenttz(lastSyncTimeSupplier.invoices).toDate());
    if (lastSyncTimeSupplier.creditNotes)
      dates.push(momenttz(lastSyncTimeSupplier.creditNotes).toDate());
    if (!dates.length) return null;
    return momenttz(Math.max(...dates.map((d) => d.getTime()))).toDate();
  }, [lastSyncTimeSupplier]);

  useEffect(() => {
    const id = setInterval(() => setTickMs(Date.now()), 10_000);
    return () => clearInterval(id);
  }, []);

  // ── Map Redux → rows ──────────────────────────────────────────────────────
  const rows: CustomerRow[] = useMemo(
    () =>
      customers
        .filter((c) => !c.buyer?.isDeactivated && !c.buyer?.isCODCustomer)
        .map((c) => {
          const s = c.buyer.documentsSummary;
          const p90 = Number(s?.totalPastDueOver90Invoices ?? 0);
          const p61 = Number(s?.totalPastDue61To90Invoices ?? 0);
          const p31 = Number(s?.totalPastDue31To60Invoices ?? 0);
          const p1 = Number(s?.totalPastDue1To30Invoices ?? 0);
          const cur = Number(s?.totalCurrentDueInvoices ?? 0);
          const total = p90 + p61 + p31 + p1 + cur;
          const creditLimit = Number(c.buyer.creditAmount ?? 0);
          return {
            id: c.buyer.id ?? '',
            name: c.buyer.name ?? '',
            ref: c.buyer.quickbooksCustomerId ?? '',
            pastDue90: p90,
            pastDue61: p61,
            pastDue31: p31,
            pastDue1: p1,
            current: cur,
            total,
            totalPastDue: p90 + p61 + p31 + p1,
            creditLimit,
            creditUtil: creditLimit > 0 ? Math.round((total / creditLimit) * 100) : null,
            pastDuePct: 0,
            emails: (c.usersEmailsDetails ?? []).map((u) => ({
              email: u.email,
              userId: (u as any).userId ?? '',
              isStatementsEmailOptOut: (u as any).isStatementsEmailOptOut ?? false,
              isPaymentRemindersEmailOptOut: (u as any).isPaymentRemindersEmailOptOut ?? false,
            })),
            lastEmailed: c.buyer.lastEmailed ? new Date(c.buyer.lastEmailed as any) : null,
            lastLogin: toSafeISOString(c.buyer.activities?.lastLoggedIn),
            notes: (c.buyer as any).notes?.[0]?.content ?? '',
            isOverdue: p90 > 0 || p61 > 0 || p31 > 0 || p1 > 0,
            creditManagerId: (c.buyer as any).asignedCreditManagerId ?? null,
            creditManagerName: '',
            salesRepId: (c.buyer as any).salesRepId ?? null,
            branch: (c.buyer as any).branch ?? null,
            terms: c.buyer.terms ?? '',
            isBuyerOnCreditHold: (c.buyer as any).isBuyerOnCreditHold ?? false,
            isFlagged: (c.buyer as any).isFlagged ?? false,
            isAutopayActive: !!c.buyer.isAutopayActive,
            autopayFrequency: c.buyer.autopayDetails?.frequency ?? null,
            agingBuckets: (s as any)?.agingBuckets ?? [],
            lastCollectionActivity: s?.lastCollectionActivity
              ? {
                  date: toSafeISOString(s.lastCollectionActivity.date),
                  type: s.lastCollectionActivity.activityType ?? null,
                  summaryLine: s.lastCollectionActivity.summaryLine ?? null,
                  countLast30Days: Number(s.lastCollectionActivity.countLast30Days ?? 0),
                }
              : null,
            dso: (c.buyer as any).dsoSummary?.dso ?? null,
            dsoTrend: (c.buyer as any).dsoSummary?.trend ?? null,
            dsoChangeInDays: (c.buyer as any).dsoSummary?.changeInDays ?? null,
            paymentBehaviorSummary: (c.buyer as any).paymentBehavior?.summary ?? null,
            paymentBehaviorConfidence: (c.buyer as any).paymentBehavior?.confidence ?? null,
          };
        }),
    [customers]
  );

  // ── Totals ────────────────────────────────────────────────────────────────
  const T = useMemo(
    () => ({
      p90: rows.reduce((s, r) => s + r.pastDue90, 0),
      p61: rows.reduce((s, r) => s + r.pastDue61, 0),
      p31: rows.reduce((s, r) => s + r.pastDue31, 0),
      p1: rows.reduce((s, r) => s + r.pastDue1, 0),
      cur: rows.reduce((s, r) => s + r.current, 0),
      tot: rows.reduce((s, r) => s + r.total, 0),
      creditLimit: rows.reduce((s, r) => s + r.creditLimit, 0),
    }),
    [rows]
  );

  const totalOver = T.p90 + T.p61 + T.p31 + T.p1;

  const isUsingAgingRanges = useMemo(
    () =>
      !!(
        (currentSupplier as any)?.isUseAgingRanges &&
        (currentSupplier as any)?.agingRanges?.length > 0
      ),
    [currentSupplier?.id]
  );

  const agingRanges = useMemo<AgingRange[]>(() => {
    if (!isUsingAgingRanges) return [];
    const ranges = (currentSupplier as any)?.agingRanges as AgingRange[] | undefined;
    if (ranges && ranges.length > 0) return [...ranges].sort((a, b) => a.order - b.order);
    return DEFAULT_AGING_RANGES;
  }, [isUsingAgingRanges, currentSupplier?.id]);

  const creditManagers: { id: string; firstName: string; lastName: string; email: string }[] =
    useMemo(
      () =>
        (supplierUsers ?? [])
          .filter((u) => u.roles.includes('SUB_SUPPLIER') || u.roles.includes('SUPPLIER'))
          .map((u) => ({
            id: u.id,
            firstName: u.firstName ?? '',
            lastName: u.lastName ?? '',
            email: u.email,
          })),
      [supplierUsers]
    );

  const rowsWithPct: CustomerRow[] = useMemo(
    () =>
      rows.map((r) => {
        const rowPastDue = r.pastDue90 + r.pastDue61 + r.pastDue31 + r.pastDue1;
        const cm = creditManagers.find((c) => c.id === r.creditManagerId);
        return {
          ...r,
          pastDuePct: totalOver > 0 ? Math.round((rowPastDue / totalOver) * 10000) / 100 : 0,
          creditManagerName: cm ? `${cm.firstName} ${cm.lastName}` : '',
        };
      }),
    [rows, totalOver, creditManagers]
  );

  // ── Quick-filter option lists + derived filtering ───────────────────────────────────────────

  const branchOptions = useMemo(() => {
    const unique = new Set<string>();
    rows.forEach((r) => {
      if (r.branch) unique.add(r.branch);
    });
    return Array.from(unique)
      .sort((a, b) => a.localeCompare(b))
      .map((b) => ({ label: b, value: b }));
  }, [rows]);

  const creditManagerOptions = useMemo(
    () =>
      creditManagers.map((cm) => ({
        label: `${cm.firstName} ${cm.lastName}`.trim() || cm.email,
        value: cm.id,
      })),
    [creditManagers]
  );

  // Buyer ids with a currently ACTIVE promise to pay - there's no such flag on the buyer doc
  // itself, so this is cross-referenced from the supplier's live promises (see the effect above).
  const activePromiseBuyerIds = useMemo(
    () => new Set(supplierPromiseToPays.filter((p) => p.status === 'ACTIVE').map((p) => p.buyerId)),
    [supplierPromiseToPays]
  );

  const isQuickFilterActive =
    quickFilters.buckets.size > 0 ||
    quickFilters.branches.size > 0 ||
    quickFilters.creditManagerIds.size > 0 ||
    quickFilters.promiseToPay.size > 0 ||
    quickFilters.flagged.size > 0;

  // Applied client-side, on top of whatever pages of customers have loaded so far - same as the
  // table's own search box already does. Within each group, selections OR together (any selected
  // value matches); the different groups (buckets/branch/credit manager/promise to pay/flagged)
  // AND together - same semantics as the "Filters" dropdown on the Credit Applications page.
  const quickFilteredRows: CustomerRow[] = useMemo(() => {
    if (!isQuickFilterActive) return rowsWithPct;
    return rowsWithPct.filter((row) => {
      if (quickFilters.buckets.size > 0) {
        const matchesABucket = Array.from(quickFilters.buckets).some((key) => {
          const amount = row[key];
          if (amount <= 0) return false;
          const range = quickFilters.bucketAmountRanges[key];
          if (range?.min != null && amount < range.min) return false;
          if (range?.max != null && amount > range.max) return false;
          return true;
        });
        if (!matchesABucket) return false;
      }
      if (quickFilters.branches.size > 0) {
        if (!row.branch || !quickFilters.branches.has(row.branch)) return false;
      }
      if (quickFilters.creditManagerIds.size > 0) {
        if (!row.creditManagerId || !quickFilters.creditManagerIds.has(row.creditManagerId)) {
          return false;
        }
      }
      if (quickFilters.promiseToPay.size > 0) {
        const hasActive = activePromiseBuyerIds.has(row.id);
        const matchesPromise = Array.from(quickFilters.promiseToPay).some((value) =>
          value === 'HAS_ACTIVE' ? hasActive : !hasActive
        );
        if (!matchesPromise) return false;
      }
      if (quickFilters.flagged.size > 0) {
        const matchesStatus = Array.from(quickFilters.flagged).some((value) =>
          value === 'AUTOPAY' ? row.isAutopayActive : row.isFlagged
        );
        if (!matchesStatus) return false;
      }
      return true;
    });
  }, [rowsWithPct, quickFilters, isQuickFilterActive, activePromiseBuyerIds]);

  // ── Totals for whatever's currently visible ──────────────────────────────────────────────
  // Distinct from T/totalOver above (which stay based on the FULL unfiltered `rows`, since each
  // row's own pastDuePct is "this customer's share of the whole book" and shouldn't jump to a
  // different scale just because a filter/view is applied). These instead drive the summary
  // cards and column footers, which should reflect only what's actually shown in the table.
  const filteredTotals = useMemo(
    () => ({
      p90: quickFilteredRows.reduce((s, r) => s + r.pastDue90, 0),
      p61: quickFilteredRows.reduce((s, r) => s + r.pastDue61, 0),
      p31: quickFilteredRows.reduce((s, r) => s + r.pastDue31, 0),
      p1: quickFilteredRows.reduce((s, r) => s + r.pastDue1, 0),
      cur: quickFilteredRows.reduce((s, r) => s + r.current, 0),
      tot: quickFilteredRows.reduce((s, r) => s + r.total, 0),
      creditLimit: quickFilteredRows.reduce((s, r) => s + r.creditLimit, 0),
      pastDuePct: quickFilteredRows.reduce((s, r) => s + r.pastDuePct, 0),
    }),
    [quickFilteredRows]
  );
  const filteredTotalOver =
    filteredTotals.p90 + filteredTotals.p61 + filteredTotals.p31 + filteredTotals.p1;

  const filteredAgingBucketTotals = useMemo(
    () =>
      agingRanges.map((range) => ({
        order: range.order,
        label: range.label,
        amount: quickFilteredRows.reduce((sum, row) => {
          const bucket = row.agingBuckets.find((b) => b.order === range.order);
          return sum + (bucket?.amount ?? 0);
        }, 0),
      })),
    [agingRanges, quickFilteredRows]
  );

  // Single handler backing the consolidated "Filters" dropdown (mirrors the Credit Applications
  // page's own filter dropdown) - `selected` is keyed by group id, each value an array of the
  // checked options' `value`s within that group.
  const handleQuickFilterChange = (selected: { [groupId: string]: { value: string }[] }) => {
    const buckets = new Set((selected.buckets ?? []).map((o) => o.value as AgingBucketKey));
    setQuickFilters((prev) => {
      // This dropdown only selects buckets, not their per-bucket $ ranges (those are set via the
      // New/Edit Collection View form) - prune a range the moment its bucket is deselected here,
      // rather than leaving it dangling and invisible.
      const bucketAmountRanges = { ...prev.bucketAmountRanges };
      Object.keys(bucketAmountRanges).forEach((key) => {
        if (!buckets.has(key as AgingBucketKey)) delete bucketAmountRanges[key as AgingBucketKey];
      });
      return {
        ...prev,
        buckets,
        bucketAmountRanges,
        branches: new Set((selected.branches ?? []).map((o) => o.value)),
        creditManagerIds: new Set((selected.creditManagerIds ?? []).map((o) => o.value)),
        promiseToPay: new Set(
          (selected.promiseToPay ?? []).map((o) => o.value as PromiseToPayFilterValue)
        ),
        flagged: new Set((selected.flagged ?? []).map((o) => o.value as FlagFilterValue)),
      };
    });
  };

  const clearQuickFilters = () => {
    setQuickFilters(EMPTY_QUICK_FILTERS);
  };

  // ── Collection Views: switching, creating, renaming, deleting ────────────────────────────

  const collectionViewsWithSummary: CustomerCollectionViewWithSummary[] = useMemo(
    () =>
      collectionViews.map((view) => ({
        ...view,
        summary: describeStoredFilters(view.filters, creditManagerOptions),
      })),
    [collectionViews, creditManagerOptions]
  );

  const activeView = activeViewId ? collectionViews.find((v) => v.id === activeViewId) : null;

  // True once the working filters diverge from what's actually saved on the active view - a
  // view's filters only ever change when this is true and "Save view" is clicked (see
  // handleSaveActiveViewFilters below), never automatically, so switching away and back without
  // saving discards the in-progress edit.
  const isActiveViewDirty = activeView
    ? !areStoredFiltersEqual(quickFiltersToStored(quickFilters), activeView.filters)
    : false;

  const handleSelectView = (viewId: string | null) => {
    if (viewId === activeViewId) return;
    setActiveViewId(viewId);
    if (viewId === null) {
      // "All Customers" always starts unfiltered - see the quickFilters state comment above.
      setQuickFilters(EMPTY_QUICK_FILTERS);
      return;
    }
    const view = collectionViews.find((v) => v.id === viewId);
    setQuickFilters(view ? storedToQuickFilters(view.filters) : EMPTY_QUICK_FILTERS);
  };

  const handleSaveActiveViewFilters = async () => {
    if (!activeViewId || !currentSupplier?.id) return;
    await dispatch(
      updateCustomerCollectionViewThunk({
        supplierId: currentSupplier.id,
        viewId: activeViewId,
        data: { filters: quickFiltersToStored(quickFilters) },
      })
    );
  };

  const handleCreateView = async (name: string, filters: StoredQuickFilters) => {
    const trimmed = name.trim();
    if (!trimmed || !currentSupplier?.id || !user?.id) return;
    const data: CustomerCollectionViewCreate = {
      supplierId: currentSupplier.id,
      userId: user.id,
      name: trimmed,
      filters,
    };
    const res = await dispatch(
      createCustomerCollectionViewThunk({ supplierId: currentSupplier.id, data })
    );
    if (res.type === 'customerCollectionViews/createCustomerCollectionView/fulfilled') {
      const payload = res.payload as { success?: boolean; data?: CustomerCollectionView } | null;
      if (payload?.success && payload.data) {
        setActiveViewId(payload.data.id);
        setQuickFilters(storedToQuickFilters(filters));
      }
    }
    setCreateViewModalPrefill(null);
  };

  const handleRenameView = (viewId: string, name: string) => {
    const trimmed = name.trim();
    if (!trimmed || !currentSupplier?.id) return;
    dispatch(
      updateCustomerCollectionViewThunk({
        supplierId: currentSupplier.id,
        viewId,
        data: { name: trimmed },
      })
    );
  };

  const handleDeleteView = (viewId: string) => {
    if (!currentSupplier?.id) return;
    dispatch(deleteCustomerCollectionViewThunk({ supplierId: currentSupplier.id, viewId }));
    if (activeViewId === viewId) {
      setActiveViewId(null);
      setQuickFilters(EMPTY_QUICK_FILTERS);
    }
  };

  const handleSaveEditedView = async (
    viewId: string,
    name: string,
    filters: StoredQuickFilters
  ) => {
    const trimmed = name.trim();
    if (!trimmed || !currentSupplier?.id) return;
    await dispatch(
      updateCustomerCollectionViewThunk({
        supplierId: currentSupplier.id,
        viewId,
        data: { name: trimmed, filters },
      })
    );
    // If this view is currently active, reflect the edit in the table right away instead of
    // waiting on the next switch-to-this-view (which is what normally applies a view's filters).
    if (activeViewId === viewId) {
      setQuickFilters(storedToQuickFilters(filters));
    }
    setEditingViewIdInModal(null);
  };

  // One removable pill per active filter VALUE (not per group) - e.g. two selected branches show
  // as two separate pills, each removable on its own, mirroring the table's own native
  // "Active Filters:" chip row for column-level filters.
  const activeQuickFilterChips = useMemo(() => {
    const chips: { id: string; label: string; onRemove: () => void }[] = [];

    quickFilters.buckets.forEach((key) => {
      const bucketLabel = BUCKET_QUICK_FILTERS.find((b) => b.key === key)?.label ?? key;
      chips.push({
        id: `buckets:${key}`,
        label: bucketLabel,
        onRemove: () =>
          setQuickFilters((prev) => {
            const next = new Set(prev.buckets);
            next.delete(key);
            const nextRanges = { ...prev.bucketAmountRanges };
            delete nextRanges[key];
            return { ...prev, buckets: next, bucketAmountRanges: nextRanges };
          }),
      });

      const range = quickFilters.bucketAmountRanges[key];
      if (range && isAmountRangeActive(range)) {
        chips.push({
          id: `bucketAmountRange:${key}`,
          label: `${bucketLabel}: ${formatAmountRangeLabel(range)}`,
          onRemove: () =>
            setQuickFilters((prev) => {
              const nextRanges = { ...prev.bucketAmountRanges };
              delete nextRanges[key];
              return { ...prev, bucketAmountRanges: nextRanges };
            }),
        });
      }
    });

    quickFilters.branches.forEach((branch) => {
      chips.push({
        id: `branches:${branch}`,
        label: `Branch: ${branch}`,
        onRemove: () =>
          setQuickFilters((prev) => {
            const next = new Set(prev.branches);
            next.delete(branch);
            return { ...prev, branches: next };
          }),
      });
    });

    quickFilters.creditManagerIds.forEach((id) => {
      const cmLabel = creditManagerOptions.find((cm) => cm.value === id)?.label ?? id;
      chips.push({
        id: `creditManagerIds:${id}`,
        label: `Credit Manager: ${cmLabel}`,
        onRemove: () =>
          setQuickFilters((prev) => {
            const next = new Set(prev.creditManagerIds);
            next.delete(id);
            return { ...prev, creditManagerIds: next };
          }),
      });
    });

    quickFilters.promiseToPay.forEach((value) => {
      chips.push({
        id: `promiseToPay:${value}`,
        label: PROMISE_TO_PAY_QUICK_FILTERS.find((p) => p.value === value)?.label ?? value,
        onRemove: () =>
          setQuickFilters((prev) => {
            const next = new Set(prev.promiseToPay);
            next.delete(value);
            return { ...prev, promiseToPay: next };
          }),
      });
    });

    quickFilters.flagged.forEach((value) => {
      chips.push({
        id: `flagged:${value}`,
        label: FLAG_QUICK_FILTERS.find((f) => f.value === value)?.label ?? value,
        onRemove: () =>
          setQuickFilters((prev) => {
            const next = new Set(prev.flagged);
            next.delete(value);
            return { ...prev, flagged: next };
          }),
      });
    });

    return chips;
  }, [quickFilters, creditManagerOptions]);

  const pastDueCount = quickFilteredRows.filter((r) => r.isOverdue).length;
  const overCreditCount = quickFilteredRows.filter(
    (r) => r.creditUtil !== null && r.creditUtil > 100
  ).length;
  const pctArPastDue =
    filteredTotals.tot > 0 ? Math.round((filteredTotalOver / filteredTotals.tot) * 100) : 0;
  // The server's customerCount includes COD customers, but we filter them out of `rows`.
  // Track how many COD customers have loaded so the count and loadingMore check exclude them.
  const codCustomerCount = useMemo(
    () => customers.filter((c) => c.buyer?.isCODCustomer).length,
    [customers]
  );
  const effectiveCustomerCount = Math.max(0, customerCount - codCustomerCount);
  // True while background batches are still arriving after the initial load
  const loadingMore = !loading && customerCount > 0 && rows.length < effectiveCustomerCount;

  // ── Data fetch ────────────────────────────────────────────────────────────
  const recursiveFetchCustomers = async (
    count: number,
    lastDockId: string,
    firstCustomers: {
      buyer: Buyer;
      usersEmailsDetails: {
        email: string;
        userId: string;
        isInvitedUser: boolean;
        isStatementsEmailOptOut: boolean;
        isPaymentRemindersEmailOptOut: boolean;
      }[];
      documentsSummary: DocumentsSummary;
    }[],
    supplierId: string
  ) => {
    if (firstCustomers.length > count) return;
    setCustomerCount(count);
    const pageSize = 100;
    let currentPrevLastDocId = lastDockId;
    for (let i = firstCustomers.length; i < count; i += pageSize) {
      // A newer fetch (e.g. after switching suppliers) supersedes this loop — stop so we
      // don't write the previous supplier's customers into the store.
      if (!supplierId || activeFetchSupplierIdRef.current !== supplierId) {
        break;
      }
      const request = dispatch(
        recursiveGetUserCustomers({
          supplierId,
          previousLastDocumentId: currentPrevLastDocId,
          pageSize,
          sortBy: 'name',
          sortOrder: 'asc',
        })
      );
      inFlightCustomersRequestRef.current = request;
      const results = await request;
      // Supplier changed while this page was in flight — abandon the rest of the loop.
      if (activeFetchSupplierIdRef.current !== supplierId) {
        break;
      }
      if (results.type === 'supplier/recursiveGetUserCustomers/fulfilled') {
        const payload = results.payload as any;
        const fetched: any[] = payload?.data ?? [];
        const lastDocumentId = payload?.lastDocumentId;
        if (lastDocumentId && lastDocumentId !== currentPrevLastDocId) {
          setPrevLastDocId(lastDocumentId);
          currentPrevLastDocId = lastDocumentId;
        }
        if (!lastDocumentId || fetched.length < pageSize) break;
      } else {
        break;
      }
    }
  };

  const handleLoadMore = async (opts?: { isFreshFetch?: boolean }) => {
    const fetchSupplierId = currentSupplier?.id || '';
    if (!fetchSupplierId) {
      return;
    }
    // If we're now fetching for a different supplier, cancel the previous chain's
    // in-flight request so its result can't merge into the new supplier's customers.
    if (activeFetchSupplierIdRef.current && activeFetchSupplierIdRef.current !== fetchSupplierId) {
      inFlightCustomersRequestRef.current?.abort();
    }
    activeFetchSupplierIdRef.current = fetchSupplierId;
    setLoading(true);
    const lastDocId = opts?.isFreshFetch ? '' : prevLastDocId;
    const request = dispatch(
      getUserCustomers({
        supplierId: fetchSupplierId,
        previousLastDocumentId: lastDocId,
        pageSize: 100,
        sortBy: 'name',
        sortOrder: 'asc',
      })
    );
    inFlightCustomersRequestRef.current = request;
    const res = await request;
    // Supplier changed while the first page was in flight — abandon this fetch.
    if (activeFetchSupplierIdRef.current !== fetchSupplierId) {
      return;
    }
    setLoading(false);
    if (res.type === 'supplier/getUserCustomers/fulfilled') {
      const payload = res.payload as any;
      if (!customerCount && payload?.customerCount) setCustomerCount(payload.customerCount);
      const newId = payload?.lastDocumentId;
      if (newId && newId !== lastDocId) {
        setPrevLastDocId(newId);
        const count = Number(payload.customerCount);
        recursiveFetchCustomers(count, newId, [...(payload.data ?? [])], fetchSupplierId);
      }
    }
  };

  useEffect(() => {
    if (mainRoles?.includes('SUPPLIER') && firebaseUserId && currentSupplier) {
      setPrevLastDocId('');
      handleLoadMore({ isFreshFetch: true });
      dispatch(getSupplierSummary({ supplierId: currentSupplier?.id || '' }));
    }
  }, [user?.id, firebaseUserId, location.pathname, currentSupplier?.id]);

  useEffect(() => {
    if (location.search.includes('refresh=true')) {
      setPrevLastDocId('');
      handleLoadMore({ isFreshFetch: true });
    }
  }, [location]);

  // Demo-only: powers the non-production "Credit Score" column's name matching.
  useEffect(() => {
    if (APP_ENVIRONMENT === 'production' || !currentSupplier?.id) return;
    dispatch(getSupplierCreditApplicationsMonitor({ supplierId: currentSupplier.id }));
  }, [currentSupplier?.id]);

  // Powers the "Has an active Promise to Pay" / "Does not have an active Promise to Pay" quick
  // filter below - there's no such flag denormalized onto the buyer doc, so this cross-references
  // the supplier's live promises instead.
  useEffect(() => {
    if (!currentSupplier?.id) return;
    dispatch(getSupplierPromiseToPays({ supplierId: currentSupplier.id }));
  }, [currentSupplier?.id, dispatch]);

  // Switching suppliers always lands back on "All Customers" with no filters, rather than
  // carrying over a view/filter selection that belongs to a different supplier's data.
  useEffect(() => {
    setActiveViewId(null);
    setQuickFilters(EMPTY_QUICK_FILTERS);
  }, [currentSupplier?.id]);

  // Loads this user's saved Collection Views for this supplier from the backend (private per
  // user - see customerCollectionViewModel.ts). Create/rename/delete/filter-edit below all
  // dispatch their own thunk and update redux state directly on success, so there's no separate
  // client-side persistence step here.
  useEffect(() => {
    if (!currentSupplier?.id) return;
    dispatch(getCustomerCollectionViews({ supplierId: currentSupplier.id }));
  }, [currentSupplier?.id, dispatch]);

  const handleToggleFlag = async (row: CustomerRow) => {
    const nextIsFlagged = !row.isFlagged;
    const res = await dispatch(
      updateSupplierBuyer({
        supplierId: currentSupplier?.id || '',
        supplierBuyerId: row.id,
        supplierBuyerDetails: {
          isFlagged: nextIsFlagged,
          ...(nextIsFlagged ? { flaggedByUserId: user?.id ?? null } : {}),
        } as any,
      })
    );
    if (res.type === 'supplierBuyers/updateSupplierBuyer/fulfilled') {
      dispatch(
        updateCustomerBuyerFlags({
          buyerIds: [row.id],
          isFlagged: nextIsFlagged,
          ...(nextIsFlagged ? { flaggedByUserId: user?.id ?? null } : {}),
        })
      );
    } else {
      openDillToast({
        message: nextIsFlagged ? 'Failed to flag customer' : 'Failed to unflag customer',
        type: 'ERROR',
      });
    }
  };

  // Flags/unflags many checkbox-selected customers in one request - the bulk-action-bar
  // counterpart to handleToggleFlag above, using the real bulk endpoint (not a per-row loop) so
  // it gives one summary toast, same shape as the bulk enroll/send-statements actions.
  const handleBulkFlag = async (selectedRows: CustomerRow[], isFlagged: boolean) => {
    const buyerIds = selectedRows.map((r) => r.id);
    if (!currentSupplier?.id || buyerIds.length === 0) return;
    const result = await dispatch(
      bulkSetBuyerFlag({ supplierId: currentSupplier.id, buyerIds, isFlagged })
    );
    const payload = result.payload as { success?: boolean; message?: string } | null;
    if (payload?.success) {
      setSelectedCustomerIds(new Set());
      dispatch(
        updateCustomerBuyerFlags({
          buyerIds,
          isFlagged,
          ...(isFlagged ? { flaggedByUserId: user?.id ?? null } : {}),
        })
      );
      openDillToast({ type: 'SUCCESS', message: payload.message || 'Done' });
    } else {
      openDillToast({
        type: 'ERROR',
        message: `Failed to ${isFlagged ? 'flag' : 'unflag'} customers`,
      });
    }
  };

  // ── RTDB listener for statement send results ─────────────────────────────
  useEffect(() => {
    const supplierId = currentSupplier?.id;
    if (!supplierId) return;
    const resultsRef = rtdbRef(myRealtimeDB, `statementSendResults/${supplierId}`);
    const unsubscribe: Unsubscribe = onValue(resultsRef, (snapshot) => {
      if (snapshot.exists()) {
        setSendResultsData(snapshot.val() as StatementSendResult);
      } else {
        setSendResultsData(null);
      }
    });
    return () => unsubscribe();
  }, [currentSupplier?.id]);

  const handleCloseResults = () => {
    const supplierId = currentSupplier?.id;
    if (supplierId) {
      remove(rtdbRef(myRealtimeDB, `statementSendResults/${supplierId}`));
      dispatch(clearStatementSendResults({ supplierId }));
    }
    setSendResultsData(null);
  };

  // ── Actions ───────────────────────────────────────────────────────────────
  const handleSendAll = async (statementId?: string) => {
    setSendAllLoading(true);
    const res = statementId
      ? await dispatch(
          sendAllBuyersStatementFromSnapshot({
            statementId,
            supplierId: currentSupplier?.id || '',
          })
        )
      : await dispatch(sendEmailStatementsForAllBuyers({ supplierId: currentSupplier?.id || '' }));
    setSendAllLoading(false);
    setOpenSendAllModal(false);
    dispatch(
      openMessageModal(
        res.type === 'supplier/sendEmailStatementsForAllBuyers/fulfilled' ||
          res.type === 'supplier/sendAllBuyersStatementFromSnapshot/fulfilled'
          ? {
              buttonText: 'Back to Dashboard',
              modalType: 'SUCCESS',
              title: 'Statements emailed!',
              subTitle: 'Your statements have been successfully emailed.',
              onClose: () => handleLoadMore({ isFreshFetch: true }),
            }
          : {
              buttonText: 'Back to Dashboard',
              modalType: 'ERROR',
              title: 'Error sending emails!',
              subTitle: String((res.payload as any) ?? 'Unknown error'),
              onClose: () => {},
            }
      )
    );
  };

  const handleOpenSendAll = () => {
    if (currentSupplier?.invoiceStatementsSettings?.isStatementSnapshotEnabled) {
      setOpenSendAllModal(true);
    } else {
      dispatch(
        openMessageModal({
          title: 'Email statements to all customers with a positive balance?',
          subTitle: 'Statements will not be sent to customers with zero or negative balance.',
          buttonText: 'No, cancel',
          modalType: 'INFO',
          customIcon: <Send size={40} />,
          customIconContainerStyle: {
            padding: '16px',
            backgroundColor: COLORS.PRIMARY_100,
            color: COLORS.PRIMARY_500,
            borderRadius: '50%',
            alignItems: 'center',
          },
          onClose: () => {},
          secondButtonText: 'Yes, email',
          isSecondButton: true,
          onSecondButtonClick: handleSendAll,
        })
      );
    }
  };

  const handleSaveSnapshot = async (month: string) => {
    setSaveSnapshotLoading(true);
    const res = await dispatch(
      saveMonthlyStatementSnapshots({ month, supplierId: currentSupplier?.id || '' })
    );
    setSaveSnapshotLoading(false);
    setOpenSaveSnapshotModal(false);
    if (res.type === 'supplier/saveMonthlyStatementSnapshots/fulfilled') {
      const payload = res.payload as any;
      openDillToast({
        type: 'SUCCESS',
        message: `Snapshots saved: ${payload?.saved ?? 0} saved, ${payload?.skipped ?? 0} skipped`,
      });
    } else {
      openDillToast({ type: 'ERROR', message: String(res.payload ?? 'Error saving snapshots') });
    }
  };

  const handleArReport = async () => {
    const res = await dispatch(
      getSupplierCustomerArReport({ supplierId: currentSupplier?.id || '' })
    );
    openDillToast(
      res.type === 'reports/getSupplierCustomerArReport/fulfilled'
        ? { type: 'SUCCESS', message: 'AR Report downloaded' }
        : { type: 'ERROR', message: String((res.payload as any) ?? 'Error downloading report') }
    );
  };

  const handleRowEmail = (row: CustomerRow) => {
    setSelectedRowForStatement(row);
    setOpenSendStatementModal(true);
  };

  const handleSendStatement = async ({
    excludedUserIds,
    additionalEmails,
    isEmailEnabled,
    mailingAddresses,
    faxNumbers,
  }: {
    excludedUserIds: string[];
    additionalEmails: string[];
    isEmailEnabled: boolean;
    mailingAddresses: DeliveryAddress[];
    faxNumbers: string[];
  }) => {
    console.log({ excludedUserIds, additionalEmails });
    if (!selectedRowForStatement) return;
    setSendStatementLoading(true);
    const res = await dispatch(
      sendEmailStatementsForBuyers({
        supplierId: currentSupplier?.id || '',
        buyerIds: [selectedRowForStatement.id],
        ...(additionalEmails.length ? { additionalEmails } : {}),
        ...(excludedUserIds.length ? { excludedUserIds } : {}),
        isEmailEnabled,
        mailingAddresses,
        faxNumbers,
      })
    );
    setSendStatementLoading(false);
    setOpenSendStatementModal(false);
    setSelectedRowForStatement(null);
    openDillToast(
      res.type === 'supplier/sendEmailStatementsForBuyers/fulfilled'
        ? { type: 'SUCCESS', message: 'Statement sent!' }
        : { type: 'ERROR', message: 'Error sending statement' }
    );
  };

  // ── Bulk actions on checkbox-selected rows (mirrors the single-row flows above) ────────────

  const handleBulkSendStatementsClick = (selectedRows: CustomerRow[]) => {
    setBulkSendStatementBuyerIds(selectedRows.map((r) => r.id));
    setOpenBulkSendStatementModal(true);
  };

  const confirmBulkSendStatements = async () => {
    if (bulkSendStatementBuyerIds.length === 0) return;
    setBulkSendStatementLoading(true);
    const res = await dispatch(
      sendEmailStatementsForBuyers({
        supplierId: currentSupplier?.id || '',
        buyerIds: bulkSendStatementBuyerIds,
      })
    );
    setBulkSendStatementLoading(false);
    setOpenBulkSendStatementModal(false);
    const count = bulkSendStatementBuyerIds.length;
    setSelectedCustomerIds(new Set());
    setBulkSendStatementBuyerIds([]);
    openDillToast(
      res.type === 'supplier/sendEmailStatementsForBuyers/fulfilled'
        ? {
            type: 'SUCCESS',
            message: `Statement${count === 1 ? '' : 's'} sent to ${count} customer${
              count === 1 ? '' : 's'
            }!`,
          }
        : { type: 'ERROR', message: 'Error sending statements' }
    );
  };

  const handleBulkEnrollClick = (selectedRows: CustomerRow[]) => {
    setBulkEnrollBuyerIds(selectedRows.map((r) => r.id));
    setBulkEnrollSequenceId(null);
    setOpenBulkEnrollModal(true);
    if (currentSupplier?.id) {
      if (collectionSequences.length === 0) {
        dispatch(getSupplierCollectionSequences({ supplierId: currentSupplier.id }));
      }
      dispatch(getSupplierCollectionTriggers({ supplierId: currentSupplier.id }));
    }
  };

  const buyerNameById = useMemo(() => Object.fromEntries(rows.map((r) => [r.id, r.name])), [rows]);

  const activeCollectionSequences = useMemo(
    () => collectionSequences.filter((s) => s.isActive),
    [collectionSequences]
  );

  // A buyer already on a trigger's target list, or already enrolled in a different sequence,
  // can't be bulk-enrolled here - the mutual-exclusion rule allows at most one engagement. Checked
  // client-side against the raw trigger/sequence data (not just the engagement index, which can
  // lag) so the user is warned and blocked before submitting, rather than finding out only after
  // the whole batch is rejected by the backend.
  const blockedBulkEnrollBuyers = useMemo(() => {
    return bulkEnrollBuyerIds
      .map((buyerId) => {
        const targetingTrigger = collectionTriggers.find((t) => triggerAppliesToBuyer(t, buyerId));
        if (targetingTrigger) {
          return {
            id: buyerId,
            name: buyerNameById[buyerId] || buyerId,
            reason: `on the “${targetingTrigger.name}” trigger’s target list`,
          };
        }
        const otherSequence = collectionSequences.find(
          (s) => s.id !== bulkEnrollSequenceId && sequenceAllowsBuyer(s, buyerId)
        );
        if (otherSequence) {
          return {
            id: buyerId,
            name: buyerNameById[buyerId] || buyerId,
            reason: `already enrolled in “${otherSequence.name}”`,
          };
        }
        return null;
      })
      .filter((b): b is { id: string; name: string; reason: string } => !!b);
  }, [
    bulkEnrollBuyerIds,
    collectionTriggers,
    collectionSequences,
    bulkEnrollSequenceId,
    buyerNameById,
  ]);

  // Adding a buyer to a sequence's `buyerIds` enrolls them immediately (see
  // CollectionSequenceFormModal) - so bulk-enrolling from here just merges the selected buyers
  // into the chosen sequence's existing list via the same update path, rather than a dedicated
  // bulk-enroll endpoint. Whole-batch: if any selected buyer is unavailable (already engaged
  // elsewhere), we block submission entirely (see blockedBulkEnrollBuyers) rather than letting the
  // backend reject the batch after the fact.
  const confirmBulkEnroll = async () => {
    if (!bulkEnrollSequenceId || !currentSupplier?.id || bulkEnrollBuyerIds.length === 0) return;
    if (blockedBulkEnrollBuyers.length > 0) return;
    const sequence = collectionSequences.find((s) => s.id === bulkEnrollSequenceId);
    if (!sequence) return;
    setBulkEnrollLoading(true);
    const mergedBuyerIds = Array.from(new Set([...sequence.buyerIds, ...bulkEnrollBuyerIds]));
    const result = await dispatch(
      updateCollectionSequence({
        supplierId: currentSupplier.id,
        sequenceId: bulkEnrollSequenceId,
        data: { buyerIds: mergedBuyerIds },
      })
    );
    const payload = result.payload as {
      success?: boolean;
      errors?: { msg?: string }[];
    } | null;
    setBulkEnrollLoading(false);
    setOpenBulkEnrollModal(false);
    setSelectedCustomerIds(new Set());
    setBulkEnrollBuyerIds([]);
    setBulkEnrollSequenceId(null);
    openDillToast(
      payload?.success
        ? { type: 'SUCCESS', message: `Enrolled in ${sequence.name}` }
        : { type: 'ERROR', message: payload?.errors?.[0]?.msg || 'Failed to enroll customers' }
    );
  };

  const handleSaveNote = async (id: string, note: string) => {
    setSavingNote(true);
    const res = await dispatch(
      updateSupplierBuyer({
        supplierId: currentSupplier?.id || '',
        supplierBuyerId: id,
        supplierBuyerDetails: { notes: [{ content: note, date: new Date() }] } as any,
      })
    );
    setSavingNote(false);
    if (res.type === 'supplierBuyers/updateSupplierBuyer/fulfilled') {
      setNoteModal(null);
      handleLoadMore({ isFreshFetch: true });
      openDillToast({ message: 'Note saved', type: 'SUCCESS' });
    } else {
      openDillToast({ message: 'Failed to save note', type: 'ERROR' });
    }
  };

  const handleSaveCreditManager = async (id: string, creditManagerId: string | null) => {
    setSavingCreditManager(true);
    const res = await dispatch(
      updateSupplierBuyer({
        supplierId: currentSupplier?.id || '',
        supplierBuyerId: id,
        supplierBuyerDetails: { asignedCreditManagerId: creditManagerId } as any,
      })
    );
    setSavingCreditManager(false);
    if (res.type === 'supplierBuyers/updateSupplierBuyer/fulfilled') {
      dispatch(
        updateCustomerBuyerAssignment({ buyerId: id, asignedCreditManagerId: creditManagerId })
      );
      setCreditManagerModal(null);
      openDillToast({ message: 'Credit Manager updated', type: 'SUCCESS' });
    } else {
      openDillToast({ message: 'Failed to update Credit Manager', type: 'ERROR' });
    }
  };

  const handleSaveSalesRep = async (id: string, salesRepId: string | null) => {
    setSavingSalesRep(true);
    const res = await dispatch(
      updateSupplierBuyer({
        supplierId: currentSupplier?.id || '',
        supplierBuyerId: id,
        supplierBuyerDetails: { salesRepId } as any,
      })
    );
    setSavingSalesRep(false);
    if (res.type === 'supplierBuyers/updateSupplierBuyer/fulfilled') {
      dispatch(updateCustomerBuyerAssignment({ buyerId: id, salesRepId }));
      setSalesRepModal(null);
      openDillToast({ message: 'Sales Rep updated', type: 'SUCCESS' });
    } else {
      openDillToast({ message: 'Failed to update Sales Rep', type: 'ERROR' });
    }
  };

  // ── Summary cards ─────────────────────────────────────────────────────────
  const summaryCards: DillSummaryCard[] = [
    {
      label: 'Customers Past Due',
      value: String(pastDueCount),
      subLabel: `of ${quickFilteredRows.length} Total Accounts`,
      icon: <Warning size={14} />,
      variant: pastDueCount > 0 ? 'danger' : 'default',
    },
    {
      label: '% AR Past Due',
      value: `${pctArPastDue}%`,
      subLabel: `${formatMoney(filteredTotalOver, 2)} Overdue`,
      icon: <Percent size={14} />,
      variant: pctArPastDue > 20 ? 'danger' : pctArPastDue > 0 ? 'warning' : 'default',
    },
    {
      label: 'Over Credit Limit',
      value: String(overCreditCount),
      subLabel: 'Utilization > 100%',
      icon: <CurrencyDollar size={14} />,
      variant: overCreditCount > 0 ? 'danger' : 'default',
    },
    {
      label: 'Total Outstanding',
      value: formatMoney(filteredTotals.tot, 2),
      subLabel: `${quickFilteredRows.length} Accounts`,
      icon: <UsersThree size={14} />,
      variant: 'default',
    },
  ];

  // ── Aging columns (dynamic or legacy) ────────────────────────────────────
  const agingCols = useMemo<DillColumn<CustomerRow>[]>(() => {
    if (isUsingAgingRanges && agingRanges.length > 0) {
      return [...agingRanges].reverse().map((range) => {
        const total = filteredAgingBucketTotals.find((t) => t.order === range.order)?.amount ?? 0;
        return {
          key: `aging_${range.order}`,
          label: `Past Due ${range.label}`,
          width: 120,
          numeric: true,
          sortable: false,
          render: (_: any, row: CustomerRow) => {
            const bucket = row.agingBuckets.find((b) => b.order === range.order);
            const amount = bucket?.amount ?? 0;
            return amount > 0 ? (
              <span className="text-red-500 font-medium">{fN(amount)}</span>
            ) : (
              <span className="text-gray-300">—</span>
            );
          },
          footer: (
            <span className="text-red-500 font-semibold text-xs">{formatMoney(total, 2)}</span>
          ),
        } as DillColumn<CustomerRow>;
      });
    }
    return [
      {
        key: 'pastDue90',
        label: 'Past Due 90+',
        width: 120,
        numeric: true,
        sortable: true,
        filterable: true,
        defaultSortDir: 'desc',
        render: (v: number) => <span className="text-red-500 font-medium">{fN(v)}</span>,
        footer: (
          <span className="text-red-500 font-semibold text-xs">
            {formatMoney(filteredTotals.p90, 2)}
          </span>
        ),
      },
      {
        key: 'pastDue61',
        label: 'Past Due 61-90',
        width: 120,
        numeric: true,
        sortable: true,
        filterable: true,
        defaultSortDir: 'desc',
        render: (v: number) => <span className="text-red-500 font-medium">{fN(v)}</span>,
        footer: (
          <span className="text-red-500 font-semibold text-xs">
            {formatMoney(filteredTotals.p61, 2)}
          </span>
        ),
      },
      {
        key: 'pastDue31',
        label: 'Past Due 31-60',
        width: 120,
        numeric: true,
        sortable: true,
        filterable: true,
        defaultSortDir: 'desc',
        render: (v: number) => <span className="text-red-500 font-medium">{fN(v)}</span>,
        footer: (
          <span className="text-red-500 font-semibold text-xs">
            {formatMoney(filteredTotals.p31, 2)}
          </span>
        ),
      },
      {
        key: 'pastDue1',
        label: 'Past Due 1-30',
        width: 120,
        numeric: true,
        sortable: true,
        filterable: true,
        defaultSortDir: 'desc',
        render: (v: number) => <span className="text-red-500 font-medium">{fN(v)}</span>,
        footer: (
          <span className="text-red-500 font-semibold text-xs">
            {formatMoney(filteredTotals.p1, 2)}
          </span>
        ),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isUsingAgingRanges,
    agingRanges,
    filteredAgingBucketTotals,
    filteredTotals.p90,
    filteredTotals.p61,
    filteredTotals.p31,
    filteredTotals.p1,
  ]);

  // ── Column definitions ────────────────────────────────────────────────────
  const columns: DillColumn<CustomerRow>[] = useMemo(
    () => [
      {
        key: 'name',
        label: 'Customer',
        width: 210,
        sticky: true,
        sortable: true,
        filterable: true,
        render: (_: any, row: CustomerRow, i: number) => (
          <div className="relative flex items-center gap-1 w-full h-full">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleToggleFlag(row);
              }}
              title={row.isFlagged ? 'Unflag this customer' : 'Flag this customer'}
              className="flex items-center justify-center bg-transparent border-none cursor-pointer p-0 shrink-0">
              <Flag
                size={16}
                weight={row.isFlagged ? 'fill' : 'regular'}
                className={row.isFlagged ? 'text-amber-500' : 'text-gray-300 hover:text-gray-400'}
              />
            </button>
            <div className="min-w-0">
              <p className="text-xs font-medium text-gray-700">
                {row.name}
                {row.ref && (
                  <span className="text-gray-400">
                    {' - '}
                    {row.ref}
                  </span>
                )}
              </p>
            </div>
            {row.isBuyerOnCreditHold && (
              <span className="absolute top-0.5 -right-3 inline-flex items-center gap-0.5 text-[8px] font-semibold rounded-full px-1 py-px bg-amber-100 text-amber-700 leading-none">
                <Warning size={7} weight="bold" />
                Credit Hold
              </span>
            )}
            {row.isAutopayActive && (
              <span
                title={
                  row.autopayFrequency
                    ? `Autopay on (${
                        row.autopayFrequency === 'dueDate' ? 'on due date' : row.autopayFrequency
                      })`
                    : 'Autopay on'
                }
                className="absolute bottom-0.5 -right-3 inline-flex items-center text-[8px] font-semibold rounded-full px-1 py-px bg-green-100 text-green-700 leading-none">
                Autopay
              </span>
            )}
          </div>
        ),
        footer: <span className="text-gray-700 font-semibold text-xs">Total</span>,
      },
      ...agingCols,
      {
        key: 'current',
        label: 'Current Due',
        width: 115,
        numeric: true,
        sortable: true,
        filterable: true,
        defaultSortDir: 'desc',
        render: (v: number) =>
          v > 0 ? (
            <span className="text-gray-700 font-medium">{fN(v)}</span>
          ) : (
            <span className="text-gray-700 font-medium">{fN(v)}</span>
          ),
        footer: (
          <span className="text-gray-700 font-semibold text-xs">
            {formatMoney(filteredTotals.cur, 2)}
          </span>
        ),
      },
      {
        key: 'total',
        label: 'Amount Due',
        width: 125,
        numeric: true,
        sortable: true,
        filterable: true,
        defaultSortDir: 'desc',
        render: (_: any, row: CustomerRow) => (
          <span className="font-semibold text-gray-700">{fN(row.total)}</span>
        ),
        footer: (
          <span className="text-gray-700 font-bold text-xs">
            {formatMoney(filteredTotals.tot, 2)}
          </span>
        ),
      },
      {
        key: 'totalPastDue',
        label: 'Total Past Due',
        width: 130,
        numeric: true,
        sortable: true,
        filterable: true,
        defaultSortDir: 'desc',
        render: (v: number) =>
          v > 0 ? (
            <span className="text-red-500 font-medium">{fN(v)}</span>
          ) : (
            <span className="text-gray-300">—</span>
          ),
        footer: (
          <span className="text-red-500 font-semibold text-xs">
            {formatMoney(filteredTotalOver, 2)}
          </span>
        ),
      },
      {
        key: 'pastDuePct',
        label: '% of Total Past Due',
        width: 145,
        numeric: true,
        sortable: true,
        filterable: true,
        defaultSortDir: 'desc',
        render: (v: number) =>
          v > 0 ? (
            <div className="flex items-center gap-2 w-full justify-end">
              <span className="text-red-500 font-medium tabular-nums w-10 text-right shrink-0">
                {v.toFixed(1)}%
              </span>
            </div>
          ) : (
            <span className="text-red-500 font-medium tabular-nums w-10 text-right shrink-0">{`${v.toFixed(
              1
            )}%`}</span>
          ),
        footer: (
          <span className="text-red-500 font-semibold text-xs">
            {filteredTotals.pastDuePct.toFixed(1)}%
          </span>
        ),
      },
      {
        key: 'creditLimit',
        label: 'Credit Limit',
        width: 115,
        numeric: true,
        sortable: true,
        filterable: true,
        defaultSortDir: 'desc',
        render: (v: number) =>
          v > 0 ? (
            <span className="text-gray-700">{fN(v, 0)}</span>
          ) : (
            <span className="text-gray-700">{`-${fN(v, 0)}`}</span>
          ),
        footer:
          filteredTotals.creditLimit > 0 ? (
            <span className="text-gray-600 font-semibold text-xs">
              {formatMoney(filteredTotals.creditLimit, 2)}
            </span>
          ) : undefined,
      },
      {
        key: 'creditUtil',
        label: '% Credit Utilized',
        width: 160,
        numeric: true,
        sortable: true,
        filterable: true,
        defaultSortDir: 'desc',
        render: (v: number | null) =>
          v === null ? <span className="text-gray-200">—</span> : <CreditUtilBar pct={v} />,
      },
      // Demo-only columns: hardcoded, deterministic-per-row placeholder values for whichever
      // rows don't have real backing data yet. Hidden outside the non-production/demo
      // environment - none of these should ever be visible in production.
      ...(APP_ENVIRONMENT !== 'production'
        ? [
            {
              key: 'creditScore',
              label: 'Credit Score',
              width: 150,
              numeric: true,
              sortable: true,
              defaultSortDir: 'desc',
              getSortValue: (row: CustomerRow) =>
                getMonitoredCreditScore(row.name, creditApplicationsMonitor) ??
                hashStringToRange(row.id, 400, 900),
              render: (_: any, row: CustomerRow) => {
                const score =
                  getMonitoredCreditScore(row.name, creditApplicationsMonitor) ??
                  hashStringToRange(row.id, 400, 900);
                return (
                  <CreditScoreGauge
                    score={score}
                    min={400}
                    max={900}
                    trend={getDemoCreditScoreTrend(row.id)}
                  />
                );
              },
            } as DillColumn<CustomerRow>,
            {
              key: 'dso',
              label: 'Average DSO',
              width: 155,
              numeric: true,
              sortable: true,
              filterable: true,
              defaultSortDir: 'desc',
              getSortValue: (row: CustomerRow) => row.dso ?? getDemoDso(row.id).dso,
              render: (_: any, row: CustomerRow) => {
                const demo = getDemoDso(row.id);
                const dso = row.dso ?? demo.dso;
                const trend = row.dsoTrend ?? demo.trend;
                const changeInDays = row.dsoChangeInDays ?? demo.changeInDays ?? 0;
                // changeInDays is signed (currentDso - priorAvg), so priorAvg = dso - changeInDays.
                const priorDso = dso - changeInDays;
                const pctChange =
                  changeInDays !== 0 && priorDso > 0
                    ? Math.round((Math.abs(changeInDays) / priorDso) * 100)
                    : null;
                return (
                  <span className="inline-flex items-center gap-1 text-gray-700">
                    {dso} days
                    {trend === 'IMPROVING' && (
                      <span
                        className="inline-flex items-center gap-0.5"
                        title={`Down ${Math.abs(changeInDays)} days${
                          pctChange !== null ? ` (${pctChange}%)` : ''
                        }`}>
                        <TrendDown size={13} weight="bold" className="text-emerald-600" />
                        {pctChange !== null && (
                          <span className="text-[10px] font-medium text-emerald-600">
                            {pctChange}%
                          </span>
                        )}
                      </span>
                    )}
                    {trend === 'DECLINING' && (
                      <span
                        className="inline-flex items-center gap-0.5"
                        title={`Up ${Math.abs(changeInDays)} days${
                          pctChange !== null ? ` (${pctChange}%)` : ''
                        }`}>
                        <TrendUp size={13} weight="bold" className="text-red-500" />
                        {pctChange !== null && (
                          <span className="text-[10px] font-medium text-red-500">{pctChange}%</span>
                        )}
                      </span>
                    )}
                    {trend === 'STABLE' && (
                      <span className="inline-flex items-center" title="Stable">
                        <Minus size={13} weight="bold" className="text-gray-400" />
                      </span>
                    )}
                  </span>
                );
              },
            } as DillColumn<CustomerRow>,
            {
              key: 'paymentBehavior',
              label: 'Payment Behavior',
              width: 220,
              sortable: true,
              filterable: true,
              getSortValue: (row: CustomerRow) =>
                row.paymentBehaviorSummary ?? getDemoPaymentBehaviorSummary(row.id),
              render: (_: any, row: CustomerRow) => (
                <span className="text-xs text-gray-600">
                  {row.paymentBehaviorSummary ?? getDemoPaymentBehaviorSummary(row.id)}
                </span>
              ),
            } as DillColumn<CustomerRow>,
          ]
        : []),
      {
        key: 'terms',
        label: 'Terms',
        width: 100,
        sortable: true,
        filterable: true,
        defaultSortDir: 'desc',
        render: (v: string | null) =>
          v === null ? (
            <span className="text-gray-200">—</span>
          ) : (
            <span className="text-gray-700">{v}</span>
          ),
      },
      {
        key: 'creditManagerName',
        label: <span className="flex items-center gap-1">Credit Manager</span>,
        width: 180,
        sortable: true,
        filterable: true,
        render: (_: string | null, row: CustomerRow) => {
          const cm = creditManagers.find((u) => u.id === row.creditManagerId);
          if (cm) {
            return (
              <button
                className="group flex items-center gap-2 text-xs text-gray-700 hover:text-blue-600 cursor-pointer px-2 py-1 rounded hover:bg-blue-50 transition-colors w-full"
                onClick={(e) => {
                  e.stopPropagation();
                  setCreditManagerModal(row);
                }}>
                <DillAvatar name={`${cm.firstName} ${cm.lastName}`} size={24} idx={0} />
                <span className="truncate">{cm.firstName}</span>
                <PencilSimple
                  size={12}
                  className="text-gray-300 group-hover:text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                />
              </button>
            );
          }
          return (
            <button
              className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-blue-600 cursor-pointer px-2 py-1 rounded border border-dashed border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors w-full"
              onClick={(e) => {
                e.stopPropagation();
                setCreditManagerModal(row);
              }}>
              <span className="text-[10px]">+</span>
              <span>Assign</span>
            </button>
          );
        },
      },
      {
        key: 'salesRepId',
        label: <span className="flex items-center gap-1">Sales Rep</span>,
        width: 180,
        sortable: true,
        filterable: true,
        getSortValue: (row: CustomerRow) =>
          salesReps.find((sp) => sp.id === row.salesRepId)?.name || '',
        render: (_: string | null, row: CustomerRow) => {
          const rep = salesReps.find((sp) => sp.id === row.salesRepId);
          if (rep) {
            return (
              <button
                className="group flex items-center gap-2 text-xs text-gray-700 hover:text-blue-600 cursor-pointer px-2 py-1 rounded hover:bg-blue-50 transition-colors w-full"
                onClick={(e) => {
                  e.stopPropagation();
                  setSalesRepModal(row);
                }}>
                <DillAvatar name={rep.name} size={24} idx={0} />
                <span className="truncate">{rep.name}</span>
                <PencilSimple
                  size={12}
                  className="text-gray-300 group-hover:text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                />
              </button>
            );
          }
          return (
            <button
              className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-blue-600 cursor-pointer px-2 py-1 rounded border border-dashed border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors w-full"
              onClick={(e) => {
                e.stopPropagation();
                setSalesRepModal(row);
              }}>
              <span className="text-[10px]">+</span>
              <span>Assign</span>
            </button>
          );
        },
      },
      {
        key: 'branch',
        label: 'Branch',
        width: 140,
        sortable: true,
        filterable: true,
        render: (v: string | null) =>
          v ? (
            <span className="text-xs text-gray-700 truncate">{v}</span>
          ) : (
            <span className="text-gray-300 text-xs">—</span>
          ),
      },
      {
        key: 'emails',
        label: (
          <span className="flex items-center gap-1">
            Emails
            <Info
              size={12}
              className="text-gray-400 cursor-pointer shrink-0"
              data-tooltip-id="emailsHeaderInfo"
            />
          </span>
        ),
        width: 200,
        render: (_: any, row: CustomerRow) => {
          if (!row.emails.length) return <span className="text-gray-300 text-xs">—</span>;
          return (
            <EmailsTooltip emails={row.emails}>
              {({ onMouseEnter, onMouseLeave, ref }) => (
                <div
                  ref={ref}
                  onMouseEnter={onMouseEnter}
                  onMouseLeave={onMouseLeave}
                  className="flex items-center gap-1.5 cursor-pointer group w-full min-w-0"
                  onClick={(e) => e.stopPropagation()}>
                  <span className="text-xs text-gray-700 truncate flex-1 min-w-0">
                    {row.emails[0].email}
                  </span>
                  <div className="flex items-center gap-0.5 shrink-0">
                    {row.emails.length > 1 && (
                      <span className="text-[10px] bg-gray-100 text-gray-500 rounded-full px-1.5 font-medium ml-0.5">
                        +{row.emails.length - 1}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </EmailsTooltip>
          );
        },
      },
      {
        key: 'lastLogin',
        label: 'Last Login',
        width: 120,
        sortable: true,
        render: (v: string | null) =>
          v ? (
            <div className="flex flex-col w-full justify-center">
              <p className="text-xs text-gray-700 text-center w-full">
                {moment(v).format('MM/DD/YYYY')}
              </p>
              <p className="text-xs text-gray-500 text-center w-full">{moment(v).format('LT')}</p>
            </div>
          ) : (
            <div className="flex flex-col w-full justify-center">
              <span className="text-gray-200 text-center w-full">—</span>
            </div>
          ),
      },
      {
        key: 'lastCollectionActivity',
        label: 'Collections Activity',
        width: 160,
        sortable: true,
        getSortValue: (row: CustomerRow) =>
          row.lastCollectionActivity?.date
            ? new Date(row.lastCollectionActivity.date).getTime()
            : 0,
        render: (_: any, row: CustomerRow) =>
          row.lastCollectionActivity?.date ? (
            <button
              className="group flex flex-col w-full justify-center cursor-pointer rounded-lg border border-transparent px-2 py-1 hover:bg-blue-50 hover:border-blue-100 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                setCollectionActivityModal(row);
              }}>
              <p className="flex items-center justify-center gap-1 text-xs text-blue-600 group-hover:underline text-center w-full">
                {moment(row.lastCollectionActivity.date).format('MM/DD/YYYY')}
                {row.lastCollectionActivity.type
                  ? ` · ${
                      COLLECTION_ACTIVITY_TYPE_LABELS[row.lastCollectionActivity.type] ??
                      row.lastCollectionActivity.type
                    }`
                  : ''}
                <Eye size={12} className="text-blue-300 group-hover:text-blue-500 shrink-0" />
              </p>
              <p className="text-xs text-gray-500 text-center w-full">
                {row.lastCollectionActivity.countLast30Days} in last 30 days
              </p>
            </button>
          ) : (
            <div className="flex flex-col w-full justify-center">
              <span className="text-gray-200 text-center w-full">—</span>
            </div>
          ),
      },
      {
        key: 'id',
        label: '',
        width: 180,
        sortable: false,
        stickyRight: true,
        render: (_: string, row: CustomerRow) => (
          <div
            className="flex flex-col items-center gap-1 py-1"
            onClick={(e) => e.stopPropagation()}>
            <DillButton
              label="Send Statement"
              icon={<Mail size={12} />}
              onClick={() => handleRowEmail(row)}
            />
            {row.lastEmailed && (
              <p className="text-[10px] text-gray-400">
                Last sent {getCurrentTimezoneDate(row.lastEmailed, 'MM/DD/YYYY')}
              </p>
            )}
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      filteredTotals,
      filteredTotalOver,
      creditManagers,
      agingCols,
      creditApplicationsMonitor,
      salesReps,
    ]
  );

  // ── Toolbar ───────────────────────────────────────────────────────────────
  const syncBadge = isSupplierSyncing ? (
    <span className="text-xs text-gray-400 flex items-center gap-1">
      <svg
        className="animate-spin"
        width="11"
        height="11"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2">
        <path d="M21 12a9 9 0 1 1-6.219-8.56" />
      </svg>
      Syncing…
    </span>
  ) : lastSyncedDate ? (
    <span className="flex items-center gap-1 text-xs text-gray-500">
      <svg
        width="11"
        height="11"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-gray-400 shrink-0">
        <polyline points="23 4 23 10 17 10" />
        <polyline points="1 20 1 14 7 14" />
        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
      </svg>
      <button
        type="button"
        onClick={() => setIsSyncHistoryModalOpen(true)}
        title="View sync history"
        className="flex items-center gap-1 bg-transparent border-none p-0 cursor-pointer text-xs text-gray-500 hover:underline">
        Synced <span className="font-medium text-gray-700">{timeSince(lastSyncedDate)} ago</span>
        {(latestSyncStatus === 'FAILED' || latestSyncStatus === 'PARTIAL') && (
          <span
            className={`w-1.5 h-1.5 rounded-full shrink-0 ${
              latestSyncStatus === 'FAILED' ? 'bg-red-500' : 'bg-amber-500'
            }`}
            aria-label={
              latestSyncStatus === 'FAILED' ? 'Last sync failed' : 'Last sync was partial'
            }
          />
        )}
      </button>
      <Info
        size={13}
        className="text-gray-400 cursor-pointer shrink-0"
        data-tooltip-id="lastSyncInfo"
      />
      <ReactTooltip
        id="lastSyncInfo"
        place="left"
        variant="dark"
        opacity="100%"
        style={{ background: '#222', width: '280px', zIndex: 99999 }}>
        <p className="text-xs leading-[16px]">
          Data is synced automatically from your accounting system. Changes made there will appear
          here after the next sync.
        </p>
      </ReactTooltip>
    </span>
  ) : null;

  // const statementsSettings = (user?.userSupplier as any)?.invoiceStatementsSettings;
  // const isSnapshotEnabled =
  //   !!statementsSettings?.isStatementSnapshotEnabled &&
  //   !!statementsSettings?.isAutoStatementsDeactivated;

  // Consolidated "Filters" dropdown - mirrors the exact grouped-checkbox pattern used on the
  // Credit Applications page (DillDropDownFilter + createCheckboxItem), instead of a row of
  // separate chips/dropdowns per filter.
  const createCheckboxItem = ({
    label,
    value,
    isChecked,
    renderContent,
  }: {
    label: string;
    value: string;
    isChecked: boolean;
    renderContent?: React.ReactNode;
  }) => ({
    label,
    value,
    labelInElement: (
      <div className="flex mx-1 my-1 items-center gap-2">
        <DillCheckbox isChecked={isChecked} />
        {renderContent ?? <div className="text-black text-xs">{label}</div>}
      </div>
    ),
  });

  const quickFilterItems = useMemo(
    () => [
      {
        id: 'flagged',
        label: 'Filter by Status',
        value: FLAG_QUICK_FILTERS.map((item) =>
          createCheckboxItem({
            label: item.label,
            value: item.value,
            isChecked: quickFilters.flagged.has(item.value),
          })
        ),
      },
      {
        id: 'buckets',
        label: 'Filter by Aging Bucket',
        value: BUCKET_QUICK_FILTERS.map((item) =>
          createCheckboxItem({
            label: item.label,
            value: item.key,
            isChecked: quickFilters.buckets.has(item.key),
          })
        ),
      },
      {
        id: 'promiseToPay',
        label: 'Filter by Promise to Pay',
        value: PROMISE_TO_PAY_QUICK_FILTERS.map((item) =>
          createCheckboxItem({
            label: item.label,
            value: item.value,
            isChecked: quickFilters.promiseToPay.has(item.value),
          })
        ),
      },
      ...(branchOptions.length > 0
        ? [
            {
              id: 'branches',
              label: 'Filter by Branch',
              value: branchOptions.map((item) =>
                createCheckboxItem({
                  label: item.label,
                  value: item.value,
                  isChecked: quickFilters.branches.has(item.value),
                })
              ),
            },
          ]
        : []),
      ...(creditManagerOptions.length > 0
        ? [
            {
              id: 'creditManagerIds',
              label: 'Filter by Credit Manager',
              value: creditManagerOptions.map((item) =>
                createCheckboxItem({
                  label: item.label,
                  value: item.value,
                  isChecked: quickFilters.creditManagerIds.has(item.value),
                })
              ),
            },
          ]
        : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [quickFilters, branchOptions, creditManagerOptions]
  );

  const quickFilterMultiValue = useMemo(
    () => ({
      flagged: Array.from(quickFilters.flagged),
      buckets: Array.from(quickFilters.buckets),
      promiseToPay: Array.from(quickFilters.promiseToPay),
      branches: Array.from(quickFilters.branches),
      creditManagerIds: Array.from(quickFilters.creditManagerIds),
    }),
    [quickFilters]
  );

  // Always present, so the queue is discoverable even while it is empty — opening it then shows
  // the "nothing pending" state rather than the button simply not existing. It sits in the
  // toolbar's left group, which is `flex-wrap`, so it drops to a new line instead of squeezing
  // the search field or the statement actions on the right.
  const accessRequestsButton = (
    <div className="relative shrink-0">
      <DillButton
        label="Access Requests"
        variant="secondary"
        icon={<UserCirclePlus size={15} />}
        onClick={() => setIsUserLoginAttemptsModalOpen(true)}
      />
      {supplierPendingUserLoginAttemptsCount > 0 && (
        <div className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-gradient-to-r from-orange-400 to-red-400 shadow-sm flex items-center justify-center pointer-events-none">
          <span className="text-2xs font-semibold text-white">
            {supplierPendingUserLoginAttemptsCount}
          </span>
        </div>
      )}
    </div>
  );

  const toolbarLeft = (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="w-[220px]">
        <DillDropDownFilter
          placeholder="Filters"
          closeMenu={false}
          isMulti
          showSelectedItems
          multiValue={quickFilterMultiValue}
          items={quickFilterItems}
          showMinimalDesign
          onMultiSelectChange={handleQuickFilterChange}
          inputHeight={32}
        />
      </div>
      {accessRequestsButton}
    </div>
  );

  // Per-value removable pills for the active quick filters - rendered via DillTable's
  // `belowToolbar` slot so it sits between the search/filters toolbar and the table body, not
  // above the whole table. Renders nothing when no filter is active. The selection is
  // auto-saved to localStorage as it changes (see the effects above), so there's no explicit
  // "save" step.
  const quickFiltersBar =
    activeQuickFilterChips.length > 0 || isActiveViewDirty ? (
      <div className="flex items-center gap-2 flex-wrap border-b border-gray-100">
        {activeQuickFilterChips.length > 0 && (
          <span className="text-xs text-gray-400 ml-1">Active Filters:</span>
        )}
        {activeQuickFilterChips.map((chip) => (
          <span
            key={chip.id}
            className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
            {chip.label}
            <button
              type="button"
              onClick={chip.onRemove}
              title="Remove this filter"
              className="flex items-center justify-center text-blue-500 hover:text-blue-700 bg-transparent border-none cursor-pointer p-0">
              <X size={11} />
            </button>
          </span>
        ))}
        {/* "All Customers": save the current ad hoc filters as a brand-new view. */}
        {!activeViewId && isQuickFilterActive && (
          <button
            type="button"
            onClick={() => setCreateViewModalPrefill('current')}
            className="text-xs font-medium text-blue-600 hover:text-blue-700 bg-transparent border-none cursor-pointer p-0 ml-1">
            Save view
          </button>
        )}
        {/* A saved view is active and its filters have changed - persist those changes onto it. */}
        {activeViewId && isActiveViewDirty && (
          <button
            type="button"
            onClick={handleSaveActiveViewFilters}
            disabled={isUpdatingCollectionView}
            className="text-xs font-medium text-blue-600 hover:text-blue-700 bg-transparent border-none cursor-pointer p-0 ml-1 disabled:opacity-50 disabled:cursor-not-allowed">
            {isUpdatingCollectionView ? 'Saving…' : 'Save changes to this view'}
          </button>
        )}
        {isQuickFilterActive && (
          <button
            type="button"
            onClick={clearQuickFilters}
            className="text-xs font-medium text-gray-400 hover:text-gray-600 bg-transparent border-none cursor-pointer p-0">
            Clear filters
          </button>
        )}
      </div>
    ) : null;

  const bulkActions = useMemo(
    () => [
      {
        key: 'sendStatements',
        label: 'Send Statements',
        onClick: (selectedRows: CustomerRow[]) => handleBulkSendStatementsClick(selectedRows),
      },
      {
        key: 'enrollSequence',
        label: 'Enroll in Collection Sequence',
        onClick: (selectedRows: CustomerRow[]) => handleBulkEnrollClick(selectedRows),
      },
      {
        key: 'flag',
        label: 'Flag',
        onClick: (selectedRows: CustomerRow[]) => handleBulkFlag(selectedRows, true),
      },
      {
        key: 'unflag',
        label: 'Unflag',
        onClick: (selectedRows: CustomerRow[]) => handleBulkFlag(selectedRows, false),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentSupplier?.id, collectionSequences]
  );

  const toolbarRight = (
    <div className="flex items-center gap-2">
      <DillButton
        variant="icon-default"
        onClick={() => setOpenGetPortalLinkModal(true)}
        icon={<LinkIcon size={16} color={COLORS.GREY_500} />}
        height={36}
        width={36}
      />
      {rows.length > 0 && (
        <>
          {/* {isSnapshotEnabled && (
            <DillButton
              label="Save Snapshots"
              variant="secondary"
              icon={<FileText size={12} />}
              onClick={() => setOpenSaveSnapshotModal(true)}
            />
          )} */}
          <DillButton
            label={sendAllLoading ? 'Sending…' : 'Send All Statements'}
            icon={<Send size={12} />}
            onClick={handleOpenSendAll}
            isLoading={sendAllLoading}
            isDisabled={sendAllLoading}
          />
          <DillButton
            label="NACM Report"
            variant="secondary"
            icon={<ArrowCircleDown size={13} />}
            onClick={handleArReport}
          />
        </>
      )}
    </div>
  );

  return (
    <div className="w-full h-full p-4 overflow-hidden">
      <ReactTooltip
        id="emailsHeaderInfo"
        place="right"
        variant="dark"
        opacity="100%"
        style={{ background: '#222', width: '280px', zIndex: 99999 }}>
        <p className="text-xs leading-[16px]">
          If you'd like to add or remove an email for a customer, please do so in your accounting
          system, and any changes will be reflected in the next sync.
        </p>
      </ReactTooltip>
      {noteModal && (
        <NoteModal
          row={noteModal}
          onClose={() => setNoteModal(null)}
          onSave={handleSaveNote}
          saving={savingNote}
        />
      )}
      {collectionActivityModal && (
        <CollectionActivityModal
          row={collectionActivityModal}
          buyer={customers.find((c) => c.buyer.id === collectionActivityModal.id)?.buyer}
          onClose={() => setCollectionActivityModal(null)}
        />
      )}

      {creditManagerModal && (
        <CreditManagerModal
          row={creditManagerModal}
          creditManagers={creditManagers}
          onClose={() => setCreditManagerModal(null)}
          onSave={handleSaveCreditManager}
          saving={savingCreditManager}
        />
      )}

      {salesRepModal && (
        <SalesRepModal
          row={salesRepModal}
          salesReps={salesReps}
          onClose={() => setSalesRepModal(null)}
          onSave={handleSaveSalesRep}
          saving={savingSalesRep}
        />
      )}

      <AppGetPortalLinkModal
        open={openGetPortalLinkModal}
        handleClose={() => setOpenGetPortalLinkModal(false)}
      />

      <AppSendStatementModal
        open={openSendStatementModal}
        handleClose={() => {
          setOpenSendStatementModal(false);
          setSelectedRowForStatement(null);
        }}
        onSubmit={handleSendStatement}
        loading={sendStatementLoading}
        deliverySettings={
          customers.find((c) => c.buyer?.id === selectedRowForStatement?.id)?.buyer
            ?.statementDeliverySettings
        }
        buyerUsers={(selectedRowForStatement?.emails ?? []).map((e) => ({
          email: e.email,
          userId: e.userId,
          isStatementsEmailOptOut: e.isStatementsEmailOptOut,
        }))}
      />

      <AppSendAllStatementsModal
        open={openSendAllModal}
        handleClose={() => setOpenSendAllModal(false)}
        onSubmit={handleSendAll}
        loading={sendAllLoading}
        savedStatementMonths={
          (currentSupplier?.invoiceStatementsSettings as any)?.savedStatementMonths ?? []
        }
      />

      <AppSaveStatementSnapshotModal
        open={openSaveSnapshotModal}
        handleClose={() => setOpenSaveSnapshotModal(false)}
        onSubmit={handleSaveSnapshot}
        loading={saveSnapshotLoading}
      />

      <AppStatementSendResultsModal
        open={!!sendResultsData}
        result={sendResultsData}
        onClose={handleCloseResults}
      />

      <DillModal
        open={openBulkSendStatementModal}
        onClose={() => setOpenBulkSendStatementModal(false)}
        title="Send Statements"
        maxWidth="min(90vw, 420px)"
        footer={
          <>
            <DillButton
              label="Cancel"
              variant="secondary"
              onClick={() => setOpenBulkSendStatementModal(false)}
              isDisabled={bulkSendStatementLoading}
            />
            <DillButton
              label={bulkSendStatementLoading ? 'Sending…' : 'Send Statements'}
              onClick={confirmBulkSendStatements}
              isLoading={bulkSendStatementLoading}
              isDisabled={bulkSendStatementLoading}
            />
          </>
        }>
        <p className="text-sm text-gray-600 m-0">
          Send a statement to {bulkSendStatementBuyerIds.length} selected customer
          {bulkSendStatementBuyerIds.length === 1 ? '' : 's'}?
        </p>
      </DillModal>

      <DillModal
        open={openBulkEnrollModal}
        onClose={() => setOpenBulkEnrollModal(false)}
        title="Enroll in Collection Sequence"
        maxWidth="min(90vw, 420px)"
        footer={
          <>
            <DillButton
              label="Cancel"
              variant="secondary"
              onClick={() => setOpenBulkEnrollModal(false)}
              isDisabled={bulkEnrollLoading}
            />
            {activeCollectionSequences.length > 0 && (
              <DillButton
                label={bulkEnrollLoading ? 'Enrolling…' : 'Enroll'}
                onClick={confirmBulkEnroll}
                isLoading={bulkEnrollLoading}
                isDisabled={
                  bulkEnrollLoading || !bulkEnrollSequenceId || blockedBulkEnrollBuyers.length > 0
                }
              />
            )}
          </>
        }>
        <div className="flex flex-col gap-2">
          {activeCollectionSequences.length === 0 ? (
            <div className="flex flex-col items-center gap-2 px-2 py-6 text-center">
              <p className="text-sm text-gray-500 m-0">
                No active collection sequences have been set up yet.
              </p>
              <DillButton
                label="Set up a Collection Sequence"
                variant="secondary"
                onClick={() => {
                  setOpenBulkEnrollModal(false);
                  navigate(
                    buildSupplierPath(currentSupplier?.id, '/collectionSequencesAndTriggers')
                  );
                }}
              />
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-600 m-0">
                Enroll {bulkEnrollBuyerIds.length} selected customer
                {bulkEnrollBuyerIds.length === 1 ? '' : 's'} in a collection sequence directly,
                bypassing any trigger. Enrollment begins immediately.
              </p>
              <DillSelect
                value={bulkEnrollSequenceId ?? ''}
                onChange={setBulkEnrollSequenceId}
                placeholder="Choose a collection sequence…"
                options={activeCollectionSequences.map((s) => ({ label: s.name, value: s.id }))}
              />
            </>
          )}
          {blockedBulkEnrollBuyers.length > 0 && (
            <div className="flex flex-col gap-1 text-xs text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
              <p className="font-medium m-0">
                {blockedBulkEnrollBuyers.length} customer
                {blockedBulkEnrollBuyers.length === 1 ? ' is' : 's are'} already engaged elsewhere
                and can’t be enrolled:
              </p>
              <ul className="m-0 pl-4 list-disc">
                {blockedBulkEnrollBuyers.slice(0, 5).map((b) => (
                  <li key={b.id}>
                    {b.name} — {b.reason}
                  </li>
                ))}
              </ul>
              {blockedBulkEnrollBuyers.length > 5 && (
                <p className="m-0">+{blockedBulkEnrollBuyers.length - 5} more</p>
              )}
              <p className="m-0">
                Remove {blockedBulkEnrollBuyers.length === 1 ? 'this customer' : 'these customers'}{' '}
                from the selection and try again.
              </p>
            </div>
          )}
        </div>
      </DillModal>

      {currentSupplier && (
        <UserLoginAttemptsModal
          open={isUserLoginAttemptsModalOpen}
          onClose={() => setIsUserLoginAttemptsModalOpen(false)}
          supplierId={currentSupplier.id}
        />
      )}

      {currentSupplier && (
        <SyncHistoryModal
          open={isSyncHistoryModalOpen}
          onClose={() => setIsSyncHistoryModalOpen(false)}
          supplierId={currentSupplier.id}
        />
      )}

      <CollectionViewTabs
        views={collectionViewsWithSummary}
        activeViewId={activeViewId}
        onSelectView={handleSelectView}
        onRequestCreateView={() => setCreateViewModalPrefill('empty')}
        onRequestEditView={setEditingViewIdInModal}
        onRenameView={handleRenameView}
        onDeleteView={handleDeleteView}
      />

      {createViewModalPrefill && (
        <CollectionViewFormModal
          mode="create"
          initialFilters={
            createViewModalPrefill === 'current' ? quickFiltersToStored(quickFilters) : undefined
          }
          onClose={() => setCreateViewModalPrefill(null)}
          onSave={handleCreateView}
          isSaving={isCreatingCollectionView}
          branchOptions={branchOptions}
          creditManagerOptions={creditManagerOptions}
        />
      )}

      {editingViewIdInModal &&
        (() => {
          const editingView = collectionViews.find((v) => v.id === editingViewIdInModal);
          if (!editingView) return null;
          return (
            <CollectionViewFormModal
              mode="edit"
              initialName={editingView.name}
              initialFilters={editingView.filters}
              onClose={() => setEditingViewIdInModal(null)}
              onSave={(name, filters) => handleSaveEditedView(editingView.id, name, filters)}
              isSaving={isUpdatingCollectionView}
              branchOptions={branchOptions}
              creditManagerOptions={creditManagerOptions}
            />
          );
        })()}
      <div className={`h-[calc(100vh-124px)]`}>
        <DillTable<CustomerRow>
          columns={columns}
          data={quickFilteredRows}
          rowKey={(r) => r.id}
          sortable
          sortCycle="3state"
          scrollX
          cardView
          defaultSort={{ key: 'total', dir: 'desc' }}
          search
          searchPlaceholder="Search …"
          showFooter
          summaryCards={summaryCards}
          toolbarLeft={toolbarLeft}
          belowToolbar={quickFiltersBar}
          toolbarRight={toolbarRight}
          countAddon={syncBadge}
          selectable
          selectedRowIds={selectedCustomerIds}
          onSelectionChange={(selectedRows) =>
            setSelectedCustomerIds(new Set(selectedRows.map((r) => r.id)))
          }
          bulkActions={bulkActions}
          loading={loading}
          loadingMore={loadingMore}
          loadingText="Loading customers…"
          emptyMessage="No customers found"
          onRowClick={(row, _, event) => {
            if (event.metaKey) {
              window.open(
                `${buildSupplierPath(currentSupplier?.id, `/customers/${row.id}`)}`,
                '_blank'
              );
            } else {
              navigate(`${buildSupplierPath(currentSupplier?.id, `/customers/${row.id}`)}`);
            }
          }}
          totalCount={isQuickFilterActive ? undefined : effectiveCustomerCount}
          countLabel="Customers"
          virtualize
          rowHeight={60}
          striped={false}
          rowPadding={0}
        />
      </div>
    </div>
  );
};
